import { Component, HostListener, OnInit } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  CrmActionItem,
  CrmActionKind,
  CrmActionLevel,
  CrmAirCarrierLicence,
  CrmBreakdownItem,
  CrmClientNetworkStatus,
  CrmCompanyNetworkLink,
  CrmCompanySnapshot,
  CrmCompanyBilling,
  CrmContact,
  CrmContactValue,
  CrmCreateClientFormValue,
  CrmDashboardData,
  CrmDashboardSummary,
  CrmDropdownOptions,
  CrmLcNetworkInfo,
  CrmDashboardTokenLcSummary,
  CrmDashboardTokenRequestHistory,
  CrmDashboardTokenRoleSummary,
  CrmDashboardTokenSummary,
  CrmNetwork,
  CrmPrimaryNetworkOption,
  CrmVendorLcOwner
} from './crm.types';
import {
  CRM_DATE_FORMAT_OPTIONS,
  CRM_DATE_FORMAT_STRINGS,
  CrmDateFormatKey
} from './crm-components/crm-date-format-options';
import { CrmPrintMenuOption } from './crm-components/crm-print-menu.component';
import { formatPrintDate, printCrmReport, renderFieldCards, renderFieldGrid, renderMetricGrid, renderPrintSection, renderTable } from './crm-components/crm-print.utils';
import { CrmCurrentUser, CrmService } from './crm.service';
import { TabsNavigationItems } from '../common/elements/elements.types';

interface CrmTableHeader {
  headerName: string;
  field: string;
  sortable?: boolean;
}

interface CrmTagListItem {
  id: string;
  name: string;
}

interface CrmTagListUpdate {
  final?: CrmTagListItem[];
}

interface CrmManualActionDraft {
  level: CrmActionLevel;
  action: CrmActionKind | '';
  details: string;
  actionDate: string;
  dueDate: string;
}

interface CrmActionClientOption {
  id: number;
  name: string;
}

const CRM_DASHBOARD_ACTION_LEVEL_OPTIONS: CrmActionLevel[] = ['Low', 'Medium', 'High', 'Critical'];
const CRM_DASHBOARD_ACTION_KIND_OPTIONS: CrmActionKind[] = ['follow_up_phone', 'follow_up_email', 'send_invoice', 'set_up_lc', 'suspend_lc', 'other'];

interface CrmBillingDraft {
  id: number | null;
  company_id: number | null;
  billing_contact_name: string;
  billing_email: string;
  billing_phone: string;
  billingStreet1: string;
  billingStreet2: string;
  billingCity: string;
  billingRegion: string;
  billingPostalCode: string;
  billingCountry: string;
  primary_registrar_email: string;
  alternateAccountContactName: string;
  alternateAccountContactEmail: string;
  alternateAccountContactPhone: string;
  billing_instructions: string;
  last_pushed: string;
  last_pulled: string;
  created: string;
  modified: string;
}

interface CrmDashboardTableRow {
  network: CrmNetwork;
  clientName: string;
  stage: string;
  type: string;
  contactName: string;
  vendorSummary: string;
  noteSummary: string;
  tags: CrmTagListItem[];
  createdDate: string;
  modifiedDate: string;
}

interface CrmVendorFilterOption {
  value: string;
  label: string;
}

interface CrmVendorModalSummary {
  value: string;
  label: string;
  clientCount: number;
  lcCount: number;
}

type CrmActionItemSeverity = 'Low' | 'Medium' | 'High' | 'Critical';
type CrmActionModalSeverityFilter = 'all' | CrmActionItemSeverity;
type CrmActionModalSortColumn = 'severity' | 'lc' | 'issue' | 'deadline';
type CrmActionModalSortDirection = 'asc' | 'desc';
type CrmRecordModalStageFilter = 'all' | CrmNetwork['stage'];
type CrmRecordModalTypeFilter = 'all' | NonNullable<CrmNetwork['type']>;
type CrmCreateClientSectionKey = 'record' | 'primaryContact' | 'billing' | 'referral';
type CrmCreateDropdownName = keyof CrmDropdownOptions;
type CrmCreateDropdownFieldName = 'source' | 'industry' | 'tagsCsv' | 'contactTagsCsv';
type CrmDashboardViewMode = 'detailed' | 'simple';

type CrmMetricModalType = 'lcs' | 'vendor' | 'renewal' | 'tokens' | 'actions';
type CrmDashboardSupplementalRequest = 'options' | 'recordDetails' | 'actions' | 'vendors' | 'tokens';

interface CrmMetricModalState {
  type: CrmMetricModalType;
  title: string;
  description: string;
  emptyLabel: string;
  headers: string[];
  rows: string[][];
}

interface CrmTokenModalRow {
  key: string;
  lcLabel: string;
  lcDetailsLink: string[];
  lcRowspan: number;
  showLcLabel: boolean;
  requestHistory: CrmDashboardTokenRequestHistory[];
  tokenType: string;
  used: number;
  unused: number;
  total: number;
  isTotal: boolean;
}

interface CrmDashboardActionIssue {
  severity: CrmActionItemSeverity;
  lcLabel: string;
  detailsLink: string[];
  issue: string;
  deadline: string | null;
  details: string;
  sortTime: number;
}

interface CrmActionSeveritySummary {
  severity: CrmActionItemSeverity;
  count: number;
  summaryClass: string;
}

interface CrmActionItemLcTarget {
  networkID: number;
  lcLabel: string;
  network: CrmNetwork;
  detailsLink: string[];
}

interface CrmDashboardLcSnapshot {
  companyId: number;
  companyName: string;
  stage: CrmNetwork['stage'];
  type: CrmNetwork['type'];
  source: CrmNetwork['source'];
  owner_user_id: number | null;
  owner_name: string | null;
  primary_contact_name: string | null;
  billing_last_pushed: string | null;
  billing_last_pulled: string | null;
  networkInfo: CrmLcNetworkInfo | null;
  companyBilling: CrmCompanyBilling | null;
  link: CrmCompanyNetworkLink;
}

interface CrmDashboardNoLcSnapshot {
  companyId: number;
  clientName: string;
  parentCompanyName: string | null;
  stage: CrmNetwork['stage'];
  type: CrmNetwork['type'];
  source: CrmNetwork['source'];
  owner_user_id: number | null;
  owner_name: string | null;
  primary_contact_name: string | null;
}

const CRM_TYPE_OPTIONS: Array<NonNullable<CrmNetwork['type']>> = ['lead', 'customer', 'partner', 'internal'];
const EMPTY_TOKEN_SUMMARY: CrmDashboardTokenSummary = {
  total: 0,
  used: 0,
  unused: 0,
  byRole: [],
  byLc: []
};
const CRM_DASHBOARD_VIEW_MODE_STORAGE_KEY = 'crm.dashboardViewMode';
const CRM_DASHBOARD_FILTERS_STORAGE_KEY = 'crm.dashboardFilters';

interface CrmStoredDashboardFilters {
  clientSearchTerm: string;
  contactSearchTerm: string;
  selectedVendorId: string;
  selectedType: string;
  selectedStage: string;
}

function getCrmPrimaryContactFallbackName(userId: number | null): string | null {
  return userId ? `User #${userId}` : null;
}

@Component({
  selector: 'app-crm',
  templateUrl: './crm.component.html',
  styleUrls: ['./crm.scss', './crm.component.scss']
})
export class CrmComponent implements OnInit {
  private readonly storedDashboardFilters = this.getStoredDashboardFilters();
  clientSearchTerm = this.storedDashboardFilters.clientSearchTerm;
  contactSearchTerm = this.storedDashboardFilters.contactSearchTerm;
  selectedVendorId = this.storedDashboardFilters.selectedVendorId;
  lcSearchTerm = '';
  selectedType = this.storedDashboardFilters.selectedType as 'all' | CrmNetwork['type'];
  selectedStage = this.storedDashboardFilters.selectedStage as 'all' | CrmNetwork['stage'];
  selectedContactNetwork: CrmNetwork | null = null;
  selectedContactEntries: Array<{ label: string; value: string }> = [];
  selectedContactDraft: Partial<CrmContact> | null = null;
  contactModalError = '';
  isSavingContact = false;
  selectedBillingNetwork: CrmNetwork | null = null;
  selectedBillingDraft: CrmBillingDraft | null = null;
  billingModalError = '';
  isSavingBilling = false;
  showCreateClientModal = false;
  createClientError = '';
  dashboardInlineError = '';
  showAddActionModal = false;
  addActionError = '';
  isSavingClientAction = false;
  actionModalClientId: number | null = null;
  actionClientSearchTerm = '';
  readonly clientActionLevelOptions = CRM_DASHBOARD_ACTION_LEVEL_OPTIONS;
  readonly clientActionKindOptions = CRM_DASHBOARD_ACTION_KIND_OPTIONS;
  manualActionDraft: CrmManualActionDraft = this.getDefaultManualActionDraft();
  crmUser: CrmCurrentUser | null = null;
  crmLoginUsername = '';
  crmLoginPassword = '';
  crmLoginError = '';
  isCrmLoggingIn = false;
  isLoadingDashboard = true;
  isLoadingDashboardEnrichment = false;
  isLoadingDashboardOptions = false;
  isLoadingDashboardRecordDetails = false;
  isLoadingDashboardActions = false;
  isLoadingDashboardVendors = false;
  isLoadingDashboardTokens = false;
  dashboardError = '';
  isLoadingPrimaryNetworkOptions = false;
  primaryNetworkOptionsError = '';
  private livePrimaryNetworkOptions: CrmPrimaryNetworkOption[] = [];
  private primaryNetworkOptionsRequestId = 0;
  private dashboardEnrichmentRequestId = 0;
  private modalOverlayMouseDownTarget: EventTarget | null = null;
  primaryNetworkOptionsPage = 1;

  networks: CrmNetwork[] = [];
  unlinkedCompanies: CrmCompanySnapshot[] = [];
  primaryNetworkOptions: CrmPrimaryNetworkOption[] = [];
  actionQueue: CrmActionItem[] = [];
  vendorLcSummary: CrmDashboardSummary[] = [];
  vendorLcOwners: CrmVendorLcOwner[] = [];
  supportSummary: CrmDashboardSummary[] = [];
  quickbooksSummary: CrmDashboardSummary[] = [];
  marketingSummary: CrmDashboardSummary[] = [];
  adminSummary: CrmDashboardSummary[] = [];
  vendorFilterOptions: CrmVendorFilterOption[] = [];
  tokenSummary: CrmDashboardTokenSummary = EMPTY_TOKEN_SUMMARY;
  crmDropdowns: CrmDropdownOptions = {
    source: [],
    industry: [],
    client_tags: [],
    user_tags: []
  };
  deletingCreateDropdownItems = new Set<string>();
  activeCreateDropdown: CrmCreateDropdownName | null = null;
  createClientForm: CrmCreateClientFormValue = this.getDefaultCreateClientForm();
  createClientSections: Record<CrmCreateClientSectionKey, boolean> = this.getDefaultCreateClientSections();
  dashboardTableRows: CrmDashboardTableRow[] = [];
  metricModalState: CrmMetricModalState | null = null;
  tokenModalRows: CrmTokenModalRow[] = [];
  tokenModalLcSearchTerm = '';
  tokenModalTokenTypeSearchTerm = '';
  tokenModalPage = 1;
  actionModalSeverityFilter: CrmActionModalSeverityFilter = 'all';
  actionModalIssueFilter = 'all';
  actionModalLcSearchTerm = '';
  actionModalHasDueDateOnly = false;
  isActionSeverityMenuOpen = false;
  actionModalSortColumn: CrmActionModalSortColumn | null = null;
  actionModalSortDirection: CrmActionModalSortDirection = 'asc';
  actionModalPage = 1;
  actionModalIssues: CrmDashboardActionIssue[] = [];
  actionModalSeveritySummaries: CrmActionSeveritySummary[] = [];
  actionModalIssueOptions: string[] = [];
  filteredActionModalIssues: CrmDashboardActionIssue[] = [];
  paginatedActionModalIssues: CrmDashboardActionIssue[] = [];
  currentActionModalPage = 1;
  totalActionModalPages = 0;
  actionModalRangeStart = 0;
  actionModalRangeEnd = 0;
  recordModalRecordSearchTerm = '';
  recordModalLcSearchTerm = '';
  recordModalStageFilter: CrmRecordModalStageFilter = 'all';
  recordModalTypeFilter: CrmRecordModalTypeFilter = 'all';
  recordModalPage = 1;
  filteredRecordModalRows: string[][] = [];
  paginatedRecordModalRows: string[][] = [];
  currentRecordModalPage = 1;
  totalRecordModalPages = 0;
  recordModalRangeStart = 0;
  recordModalRangeEnd = 0;
  renewalModalLcSearchTerm = '';
  renewalModalSortDirection: 'asc' | 'desc' | null = null;
  renewalModalPage = 1;
  filteredRenewalModalRows: string[][] = [];
  paginatedRenewalModalRows: string[][] = [];
  currentRenewalModalPage = 1;
  totalRenewalModalPages = 0;
  renewalModalRangeStart = 0;
  renewalModalRangeEnd = 0;
  private renewalModalLcs: CrmDashboardLcSnapshot[] = [];

  dateFormat: CrmDateFormatKey = 'Default';
  dateFormatStrings = CRM_DATE_FORMAT_STRINGS;
  dateNow = new Date();
  dashboardViewMode: CrmDashboardViewMode = this.getStoredDashboardViewMode();
  dashboardViewTabs: TabsNavigationItems[] = this.buildDashboardViewTabs(this.dashboardViewMode);
  readonly dateFormatOptions = CRM_DATE_FORMAT_OPTIONS;
  readonly actionSeverityOptions: CrmActionItemSeverity[] = ['Low', 'Medium', 'High', 'Critical'];
  private readonly detailedDashboardMetricSkeletonItems = [1, 2, 3, 4, 5];
  private readonly simpleDashboardMetricSkeletonItems = [1, 2, 3];
  readonly dashboardInsightSkeletonItems = [1, 2, 3];
  readonly dashboardInsightRowSkeletonItems = [1, 2, 3, 4];
  readonly dashboardFilterSkeletonItems = [1, 2, 3, 4, 5, 6];
  readonly dashboardTableSkeletonRows = [1, 2, 3, 4, 5, 6];
  readonly dashboardTableSkeletonCells = [1, 2, 3, 4, 5, 6, 7, 8];
  readonly metricModalSkeletonRows = [1, 2, 3, 4, 5];
  readonly metricModalSkeletonCells = [1, 2, 3, 4];
  readonly actionSeveritySkeletonItems = [1, 2, 3, 4];

  readonly typeOptions = CRM_TYPE_OPTIONS;
  readonly stageOptions: CrmNetwork['stage'][] = ['prospect', 'proposal', 'trial', 'client', 'at_risk', 'paused', 'lost'];
  readonly dashboardItemsPerPage = 10;
  readonly primaryNetworkOptionsPerPage = 8;
  readonly tokenModalItemsPerPage = 10;
  readonly actionModalItemsPerPage = 10;
  readonly recordModalItemsPerPage = 10;
  readonly renewalModalItemsPerPage = 10;
  readonly allowCreateClientLcSelection = false;
  readonly dashboardTableHeaders: CrmTableHeader[] = [
    { headerName: 'Record', field: 'clientName', sortable: true },
    { headerName: 'Stage', field: 'stage', sortable: true },
    { headerName: 'Type', field: 'type', sortable: true },
    { headerName: 'Contacts', field: 'contactName', sortable: true },
    { headerName: 'Vendor', field: 'vendorSummary', sortable: true },
    { headerName: 'Record Note', field: 'noteSummary', sortable: false },
    { headerName: 'Created', field: 'createdDate', sortable: true },
    { headerName: 'Updated', field: 'modifiedDate', sortable: true }
  ];
  readonly tokenModalTableHeaders: CrmTableHeader[] = [
    { headerName: 'LC', field: 'lcLabel', sortable: false },
    { headerName: 'Token Type', field: 'tokenType', sortable: false },
    { headerName: 'Used', field: 'used', sortable: false },
    { headerName: 'Unused', field: 'unused', sortable: false },
    { headerName: 'Total', field: 'total', sortable: false }
  ];
  private metricModalTableHeaderCache = new Map<string, CrmTableHeader[]>();
  readonly dashboardTableData$ = new BehaviorSubject<CrmDashboardTableRow[]>([]);
  readonly dashboardSortingSubscriber$ = new Subject<CrmDashboardTableRow[]>();
  private savingDashboardFields = new Set<string>();
  readonly printOptions: CrmPrintMenuOption[] = [
    { id: 'dashboard-report', label: 'LC Dashboard Report' },
    { id: 'client-records-report', label: 'LC Records Report' }
  ];

  airCarrierLicences: CrmAirCarrierLicence[] = [];
  isLoadingAirCarrierLicences = false;
  isAirCarrierLicencesCollapsed = true;
  airCarrierLicencesError = '';
  airCarrierLicenceSearchTerm = '';
  airCarrierLicenceCanadianOnly = true;
  currentAirCarrierLicencePage = 1;
  readonly airCarrierLicenceItemsPerPage = 10;
  readonly airCarrierLicenceTableHeaders: CrmTableHeader[] = [
    { headerName: 'Air Carrier', field: 'airCarrierName', sortable: false },
    { headerName: 'Licence #', field: 'licenceNum', sortable: false },
    { headerName: 'Licence Type', field: 'licenceTypeDescE', sortable: false },
    { headerName: 'Class', field: 'classDescE', sortable: false },
    { headerName: 'Action', field: 'action', sortable: false }
  ];

  constructor(private crmService: CrmService) {
  }

  ngOnInit(): void {
    this.initializeCrmSession();
  }

  retryDashboardLoad(): void {
    if (!this.crmUser) {
      return;
    }

    this.loadDashboardData();
  }

  get isDetailedDashboardView(): boolean {
    return this.dashboardViewMode === 'detailed';
  }

  get dashboardMetricSkeletonItems(): number[] {
    return this.isDetailedDashboardView
      ? this.detailedDashboardMetricSkeletonItems
      : this.simpleDashboardMetricSkeletonItems;
  }

  setDashboardViewMode(mode: CrmDashboardViewMode): void {
    this.dashboardViewMode = mode;
    this.dashboardViewTabs = this.buildDashboardViewTabs(mode);
    try {
      window.localStorage.setItem(CRM_DASHBOARD_VIEW_MODE_STORAGE_KEY, mode);
    } catch (error) {
      // Ignore localStorage failures; the selected view still applies for this session.
    }
  }

  updateDashboardViewTab(item: TabsNavigationItems): void {
    this.setDashboardViewMode(item && item.name === 'Simple View' ? 'simple' : 'detailed');
  }

  get crmUserDisplayName(): string {
    if (!this.crmUser) {
      return '';
    }

    const name = `${this.crmUser.first_name || ''} ${this.crmUser.last_name || ''}`.trim();
    return name || this.crmUser.username || this.crmUser.email || `User #${this.crmUser.id}`;
  }

  submitCrmLogin(): void {
    const username = this.crmLoginUsername.trim();
    if (!username || !this.crmLoginPassword) {
      this.crmLoginError = 'Username and password are required.';
      return;
    }

    this.isCrmLoggingIn = true;
    this.crmLoginError = '';
    this.crmService.loginCrmUser(username, this.crmLoginPassword).subscribe(
      (user) => {
        this.crmUser = user;
        this.crmLoginPassword = '';
        this.isCrmLoggingIn = false;
        this.loadDashboardData();
      },
      (error) => {
        this.crmLoginError = this.requestErrorMessage(error, 'CRM login failed.');
        this.isCrmLoggingIn = false;
      }
    );
  }

  logoutCrmUser(): void {
    this.crmService.clearStoredCrmUser();
    this.crmService.clearCrmCaches();
    this.crmUser = null;
    this.crmLoginPassword = '';
    this.crmLoginError = '';
    this.dashboardError = '';
    this.dashboardInlineError = '';
    this.isLoadingDashboard = false;
    this.setDashboardSupplementalLoading(false);
    this.dashboardEnrichmentRequestId++;
    this.dashboardTableData$.next([]);
  }

  get filteredNetworks(): CrmNetwork[] {
    const clientSearch = this.clientSearchTerm.trim().toLowerCase();
    const contactSearch = this.contactSearchTerm.trim().toLowerCase();

    return this.networks.filter((network) => {
      const matchesType = this.selectedType === 'all' || network.type === this.selectedType;
      const matchesStage = this.selectedStage === 'all' || network.stage === this.selectedStage;
      const matchesVendor = this.selectedVendorId === 'all' || this.getVendorFilterValue(network) === this.selectedVendorId;
      const matchesClient = !clientSearch || this.getClientSearchTerms(network).some((value) => value.toLowerCase().includes(clientSearch));
      const matchesContact = !contactSearch || this.getContactSearchTerms(network).some((value) => value.toLowerCase().includes(contactSearch));
      return matchesType && matchesStage && matchesVendor && matchesClient && matchesContact;
    });
  }

  private buildVendorFilterOptions(networks: CrmNetwork[]): CrmVendorFilterOption[] {
    const options = new Map<string, CrmVendorFilterOption>();

    networks.forEach((network) => {
      const value = this.getVendorFilterValue(network);
      if (value === null) {
        return;
      }

      if (!options.has(value)) {
        options.set(value, {
          value,
          label: this.getVendorFilterLabel(network)
        });
      }
    });

    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  private getVendorModalSummaries(): CrmVendorModalSummary[] {
    const summaries = new Map<string, CrmVendorModalSummary>();

    this.networks.forEach((network) => {
      const value = this.getVendorFilterValue(network);
      if (value === null) {
        return;
      }

      if (!summaries.has(value)) {
        summaries.set(value, {
          value,
          label: this.getVendorFilterLabel(network),
          clientCount: 0,
          lcCount: 0
        });
      }

      const summary = summaries.get(value)!;
      summary.clientCount += 1;
      summary.lcCount += this.getAttachedLinkedNetworks(network).length;
    });

    return Array.from(summaries.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  get filteredPrimaryNetworkOptions(): CrmPrimaryNetworkOption[] {
    const search = this.lcSearchTerm.trim().toLowerCase();

    if (!search) {
      return this.primaryNetworkOptions;
    }

    return this.primaryNetworkOptions.filter((option) => {
      const searchTerms = [
        option.network_name,
        option.crm_status,
        option.notes || '',
        option.test_mode_note || '',
        option.test_mode ? 'test mode' : '',
        option.vendor ? 'vendor' : ''
      ];

      return searchTerms.some((value) => value.toLowerCase().includes(search));
    });
  }

  get currentPrimaryNetworkOptionsPage(): number {
    const totalPages = this.totalPrimaryNetworkOptionPages;

    if (totalPages === 0) {
      return 1;
    }

    return Math.min(this.primaryNetworkOptionsPage, totalPages);
  }

  get totalPrimaryNetworkOptionPages(): number {
    return Math.ceil(this.filteredPrimaryNetworkOptions.length / this.primaryNetworkOptionsPerPage);
  }

  get paginatedPrimaryNetworkOptions(): CrmPrimaryNetworkOption[] {
    const startIndex = (this.currentPrimaryNetworkOptionsPage - 1) * this.primaryNetworkOptionsPerPage;
    return this.filteredPrimaryNetworkOptions.slice(startIndex, startIndex + this.primaryNetworkOptionsPerPage);
  }

  get primaryNetworkOptionsRangeStart(): number {
    if (this.filteredPrimaryNetworkOptions.length === 0) {
      return 0;
    }

    return ((this.currentPrimaryNetworkOptionsPage - 1) * this.primaryNetworkOptionsPerPage) + 1;
  }

  get primaryNetworkOptionsRangeEnd(): number {
    return Math.min(this.currentPrimaryNetworkOptionsPage * this.primaryNetworkOptionsPerPage, this.filteredPrimaryNetworkOptions.length);
  }

  get filteredTokenModalLcs(): CrmDashboardTokenLcSummary[] {
    const lcSearch = this.tokenModalLcSearchTerm.trim().toLowerCase();
    const tokenTypeSearch = this.tokenModalTokenTypeSearchTerm.trim().toLowerCase();

    return (this.tokenSummary.byLc || []).filter((lc) => {
      const matchesLc = !lcSearch || [
        lc.networkName,
        String(lc.networkID)
      ].some((value) => value.toLowerCase().includes(lcSearch));

      const matchesTokenType = !tokenTypeSearch
        || ['total', String(lc.total || 0), String(lc.used || 0), String(lc.unused || 0)].some((value) => value.toLowerCase().includes(tokenTypeSearch))
        || this.getTokenModalRoleRows(lc).some((role) => [
          role.roleName,
          String(role.roleId),
          String(role.total || 0),
          String(role.used || 0),
          String(role.unused || 0)
        ].some((value) => value.toLowerCase().includes(tokenTypeSearch)));

      return matchesLc && matchesTokenType;
    });
  }

  get currentTokenModalPage(): number {
    const totalPages = this.totalTokenModalPages;

    if (totalPages === 0) {
      return 1;
    }

    return Math.min(this.tokenModalPage, totalPages);
  }

  get totalTokenModalPages(): number {
    return Math.ceil(this.filteredTokenModalLcs.length / this.tokenModalItemsPerPage);
  }

  get paginatedTokenModalLcs(): CrmDashboardTokenLcSummary[] {
    const startIndex = (this.currentTokenModalPage - 1) * this.tokenModalItemsPerPage;
    return this.filteredTokenModalLcs.slice(startIndex, startIndex + this.tokenModalItemsPerPage);
  }

  get tokenModalRangeStart(): number {
    if (this.filteredTokenModalLcs.length === 0) {
      return 0;
    }

    return ((this.currentTokenModalPage - 1) * this.tokenModalItemsPerPage) + 1;
  }

  get tokenModalRangeEnd(): number {
    return Math.min(this.currentTokenModalPage * this.tokenModalItemsPerPage, this.filteredTokenModalLcs.length);
  }

  get networkTypes(): Array<NonNullable<CrmNetwork['type']>> {
    return this.typeOptions.filter((type) => this.networks.some((network) => network.type === type));
  }

  get lcSnapshots(): CrmDashboardLcSnapshot[] {
    return this.networks.reduce((snapshots, network) => {
      const networkInfo = network.networkInfo || network.network_info || null;
      this.getAttachedLinkedNetworks(network).forEach((link) => snapshots.push({
        companyId: network.company_id || network.id,
        companyName: network.company_name || network.name,
        stage: network.stage,
        type: network.type,
        source: network.source,
        owner_user_id: network.owner_user_id,
        owner_name: getCrmPrimaryContactFallbackName(network.owner_user_id),
        primary_contact_name: this.getPrimaryContactName(network) || null,
        billing_last_pushed: network.company_billing ? network.company_billing.last_pushed : null,
        billing_last_pulled: networkInfo ? networkInfo.subscription_end_date : (network.company_billing ? network.company_billing.last_pulled : null),
        networkInfo,
        companyBilling: network.company_billing || null,
        link
      }));
      return snapshots;
    }, [] as CrmDashboardLcSnapshot[]);
  }

  get noLcClientSnapshots(): CrmDashboardNoLcSnapshot[] {
    const placeholderSnapshots = this.networks.reduce((snapshots, network) => {
      this.getUnattachedLinkedNetworks(network).forEach((link) => snapshots.push({
        companyId: network.id,
        clientName: link.client_name || network.name,
        parentCompanyName: network.name,
        stage: link.client_stage || network.stage,
        type: link.client_type || network.type,
        source: network.source,
        owner_user_id: network.owner_user_id,
        owner_name: getCrmPrimaryContactFallbackName(network.owner_user_id),
        primary_contact_name: this.getPrimaryContactName(network) || null
      }));
      return snapshots;
    }, [] as CrmDashboardNoLcSnapshot[]);
    const placeholderCompanyIds = new Set(placeholderSnapshots.map((snapshot) => snapshot.companyId));
    const standaloneSnapshots = this.companySnapshots
      .filter((company) => company.lc_count === 0 && !placeholderCompanyIds.has(company.id))
      .map((company) => ({
        companyId: company.id,
        clientName: company.name,
        parentCompanyName: null,
        stage: company.stage,
        type: company.type,
        source: company.source,
        owner_user_id: company.owner_user_id,
        owner_name: company.owner_name,
        primary_contact_name: company.primary_contact_name || null
      }));

    return [...placeholderSnapshots, ...standaloneSnapshots];
  }

  get totalCompanyCount(): number {
    return this.networks.length;
  }

  get standardLcCount(): number {
    return this.networks.filter((network) => !network.vendor).length;
  }

  get testModeLcCount(): number {
    return this.lcSnapshots.filter((lc) => lc.link.test_mode).length;
  }

  get vendorLcCount(): number {
    return this.getVendorModalSummaries().length;
  }

  get unownedCompanyCount(): number {
    return this.networks.filter((network) => network.owner_user_id === null).length;
  }

  get renewalCompanyCount(): number {
    return this.lcSnapshots.filter((lc) => this.isLcUpForRenewal(lc)).length;
  }

  get tokenTotalCount(): number {
    return this.tokenSummary.total || 0;
  }

  get actionItemCount(): number {
    return this.actionModalIssues.length;
  }

  getMetricModalTableHeaders(modal: CrmMetricModalState): CrmTableHeader[] {
    const cacheKey = `${modal.type}:${modal.headers.join('|')}`;
    const cachedHeaders = this.metricModalTableHeaderCache.get(cacheKey);
    if (cachedHeaders) {
      return cachedHeaders;
    }

    const headers = modal.headers.map((header, index) => ({
      headerName: header,
      field: `column${index}`,
      sortable: false
    }));
    this.metricModalTableHeaderCache.set(cacheKey, headers);
    return headers;
  }

  getMetricModalCellDetailsLink(modal: CrmMetricModalState, row: string[], columnIndex: number): string[] | null {
    if (columnIndex !== 0) {
      return null;
    }

    switch (modal.type) {
      case 'lcs':
        return this.getMetricRecordDetailsLink(row);
      case 'vendor':
        return this.getVendorMetricDetailsLink(row[0]);
      case 'renewal':
        return this.getMetricRowLcDetailsLink(row[0]);
      default:
        return null;
    }
  }

  get tokenCardBreakdown(): string {
    return `${this.tokenSummary.used || 0} used / ${this.tokenSummary.unused || 0} unused`;
  }

  isMetricCardLoading(type: CrmMetricModalType): boolean {
    switch (type) {
      case 'vendor':
        return this.isLoadingDashboardVendors;
      case 'renewal':
        return this.isLoadingDashboardRecordDetails;
      case 'tokens':
        return this.isLoadingDashboardTokens;
      case 'actions':
        return this.isLoadingDashboardActions
          || this.isLoadingDashboardRecordDetails;
      default:
        return false;
    }
  }

  isMetricModalLoading(type: CrmMetricModalType): boolean {
    return this.isMetricCardLoading(type);
  }

  get overdueActionCount(): number {
    const today = new Date();
    return this.actionQueue.filter((action) => action.status === 'open' && new Date(action.actionDate) <= today).length;
  }

  get openSupportTicketCount(): number {
    return this.getDashboardSummaryCount(this.supportSummary, 'open-support') + this.getDashboardSummaryCount(this.supportSummary, 'pending-support');
  }

  get overdueInvoiceCount(): number {
    return this.getDashboardSummaryCount(this.quickbooksSummary, 'qb-overdue');
  }

  get companyStageBreakdown(): CrmBreakdownItem[] {
    return this.buildBreakdown(
      this.networks.map((network) => network.stage),
      ['prospect', 'proposal', 'trial', 'client', 'at_risk', 'paused', 'lost']
    );
  }

  get companyTypeBreakdown(): CrmBreakdownItem[] {
    return this.buildBreakdown(
      this.networks.map((network) => network.type),
      ['lead', 'customer', 'partner', 'internal']
    );
  }

  get lcStatusBreakdown(): CrmBreakdownItem[] {
    return this.buildBreakdown(
      this.lcSnapshots.map((lc) => lc.link.crm_status),
      ['workplace', 'trainers', 'hybrid', 'vendor']
    );
  }

  get acquisitionSourceBreakdown(): CrmBreakdownItem[] {
    const dummySources = this.marketingSummary.length > 0
      ? this.marketingSummary
      : [
          { key: 'referral', label: 'Referral', count: 18 },
          { key: 'conference', label: 'Conference / Event', count: 11 },
          { key: 'web', label: 'Web Inquiry', count: 9 },
          { key: 'partner', label: 'Partner Channel', count: 6 }
        ];

    return dummySources.map((item) => ({
      key: item.key,
      label: item.label,
      count: item.count
    }));
  }

  get canCreateClient(): boolean {
    return this.hasRequiredClientFields() && this.hasValidLcSelection();
  }

  get selectedLinkedLcCount(): number {
    return this.createClientForm.linkedNetworkIds.length;
  }

  get selectedPrimaryLcName(): string | null {
    const primaryNetworkId = this.createClientForm.primaryNetworkId;

    if (primaryNetworkId === null) {
      return null;
    }

    const primaryOption = this.primaryNetworkOptions.find((option) => option.network_id === primaryNetworkId);
    return primaryOption ? primaryOption.network_name : null;
  }

  get companySnapshots(): CrmCompanySnapshot[] {
    return [
      ...this.networks.map((network) => {
        const linkedNetworks = this.getAttachedLinkedNetworks(network);
        return {
          id: network.id,
          name: network.name,
          stage: network.stage,
          type: network.type,
          source: network.source,
          owner_user_id: network.owner_user_id,
          owner_name: getCrmPrimaryContactFallbackName(network.owner_user_id),
          lc_count: linkedNetworks.length,
          lc_statuses: linkedNetworks.map((link) => link.crm_status as CrmClientNetworkStatus),
          test_mode_count: linkedNetworks.filter((link) => link.test_mode).length,
          billing_last_pushed: network.company_billing ? network.company_billing.last_pushed : null,
          billing_last_pulled: network.company_billing ? network.company_billing.last_pulled : null,
          referral_programs: network.referral_record?.program ? [network.referral_record.program] : [],
          primary_contact_name: this.getPrimaryContactName(network) || null
        };
      }),
      ...this.unlinkedCompanies
    ];
  }

  buildCardModal(type: CrmMetricModalType): void {
    if (type === 'tokens') {
      this.resetTokenModalFilters();
    } else if (type === 'actions') {
      this.tokenModalRows = [];
      this.resetActionModalFilters();
    } else {
      this.tokenModalRows = [];
    }

    this.metricModalState = this.getMetricModalState(type);

    if (type === 'lcs') {
      this.resetRecordModalFilters();
    } else if (type === 'renewal') {
      this.resetRenewalModalFilters();
    }
  }

  closeMetricModal(): void {
    this.metricModalState = null;
    this.tokenModalRows = [];
    this.tokenModalLcSearchTerm = '';
    this.tokenModalTokenTypeSearchTerm = '';
    this.tokenModalPage = 1;
    this.resetActionModalFilters();
    this.resetRecordModalFilters(false);
    this.resetRenewalModalFilters(false);
  }

  onModalOverlayMouseDown(event: MouseEvent): void {
    this.modalOverlayMouseDownTarget = event.target;
  }

  shouldCloseModalFromOverlayClick(event: MouseEvent): boolean {
    const shouldClose = this.modalOverlayMouseDownTarget === event.currentTarget
      && event.target === event.currentTarget;
    this.modalOverlayMouseDownTarget = null;

    return shouldClose;
  }

  getStatusClass(status: CrmNetwork['stage']): string {
    switch (status) {
      case 'client':
        return 'status-active';
      case 'proposal':
        return 'status-proposal';
      case 'trial':
        return 'status-hybrid';
      case 'at_risk':
        return 'status-at-risk';
      case 'paused':
        return 'status-paused';
      case 'lost':
        return 'status-lost';
      case 'prospect':
      default:
        return 'status-demo';
    }
  }

  getTypeClass(type: CrmNetwork['type']): string {
    switch (type) {
      case 'customer':
        return 'type-client';
      case 'partner':
      case 'internal':
        return 'type-opportunity';
      case 'lead':
        return 'type-lead';
      default:
        return 'type-prospect';
    }
  }

  getSelectedTypeFilterClass(): string {
    return this.selectedType === 'all' ? '' : this.getTypeClass(this.selectedType);
  }

  getSelectedStageFilterClass(): string {
    return this.selectedStage === 'all' ? '' : this.getStatusClass(this.selectedStage);
  }

  getClientDetailsLink(network: CrmNetwork): string[] {
    if (this.hasClientAttachedLc(network)) {
      return ['/crm', 'details', `${this.getNetworkId(network)}`];
    }

    const companyId = this.getCompanyId(network);
    return ['/crm', 'details', 'client', `${companyId || network.id}`];
  }

  getClientDisplayName(network: CrmNetwork): string {
    return network.company_name || network.name;
  }

  hasClientAttachedLc(network: CrmNetwork): boolean {
    return network.has_lc !== false && this.getNetworkId(network) > 0 && this.getAttachedLinkedNetworks(network).length > 0;
  }

  getCompanyId(network: CrmNetwork): number | null {
    const companyId = Number(network.company_id || (!this.hasClientAttachedLc(network) ? network.id : 0));
    return Number.isFinite(companyId) && companyId > 0 ? companyId : null;
  }

  getNetworkId(network: CrmNetwork): number {
    return network.networkID || network.network_id || 0;
  }

  getVendorId(network: CrmNetwork): number | null {
    const vendorId = network.vendorId !== undefined && network.vendorId !== null ? network.vendorId : network.vendor_id;
    if (vendorId !== undefined && vendorId !== null && vendorId > 0) {
      return vendorId;
    }

    const primaryVendorId = network.primary_network ? network.primary_network.vendorId : null;
    return primaryVendorId !== undefined && primaryVendorId !== null && primaryVendorId > 0 ? primaryVendorId : null;
  }

  getVendorSummary(network: CrmNetwork): string {
    if (network.vendorUnknown) {
      return 'No vendor';
    }

    const vendorId = this.getVendorId(network);
    if (vendorId === null) {
      return 'No vendor assigned';
    }

    const vendorName = network.vendor_name || 'Vendor LC';
    return `${vendorName} / Vendor LC #${vendorId}`;
  }

  getVendorFilterValue(network: CrmNetwork): string | null {
    const vendorId = this.getVendorId(network);
    return vendorId !== null && vendorId !== undefined ? String(vendorId) : null;
  }

  getVendorFilterLabel(network: CrmNetwork): string {
    const vendorId = this.getVendorId(network);
    if (vendorId === null) {
      return 'No vendor';
    }

    const vendorName = network.vendor_name || 'Vendor LC';
    return `${vendorName} (#${vendorId})`;
  }

  getContactEntries(contact: CrmContact | null): Array<{ label: string; value: string }> {
    if (!contact) {
      return [];
    }

    return Object.keys(contact).map((key) => ({
      label: this.formatContactFieldLabel(key),
      value: this.formatContactFieldValue(contact[key])
    }));
  }

  getPrimaryLc(network: CrmNetwork): CrmCompanyNetworkLink | null {
    const linkedNetworks = this.getAttachedLinkedNetworks(network);
    const primaryNetwork = network.primary_network && this.hasAttachedLc(network.primary_network) ? network.primary_network : null;
    return linkedNetworks.find((link) => link.is_primary) || primaryNetwork || linkedNetworks[0] || null;
  }

  isActionDue(action: CrmActionItem): boolean {
    return action.status === 'open' && new Date(action.actionDate) <= new Date();
  }

  getClientNote(network: CrmNetwork): string | null {
    return network.notes_summary || null;
  }

  getClientTags(network: CrmNetwork): string[] {
    return this.parseCsvList(network.tags_csv || '');
  }

  getClientTagItems(network: CrmNetwork): CrmTagListItem[] {
    return this.getClientTags(network).map((tag, index) => ({
      id: `${this.getCompanyId(network) || this.getNetworkId(network) || 'client'}:${index}:${tag}`,
      name: tag
    }));
  }

  getNetworkInfoNote(network: CrmNetwork): string | null {
    return network.network_info_note || (network.networkInfo ? network.networkInfo.notes : null) || (network.network_info ? network.network_info.notes : null) || null;
  }

  getLinkedLcCount(network: CrmNetwork): number {
    return this.getAttachedLinkedNetworks(network).length;
  }

  refreshDashboardTable(): void {
    this.saveDashboardFilters();
    const rows = this.filteredNetworks.map((network) => this.mapNetworkToTableRow(network));
    this.dashboardTableData$.next(rows);
    setTimeout(() => this.dashboardSortingSubscriber$.next(rows), 0);

    if (rows.length <= this.dashboardItemsPerPage) {
      setTimeout(() => {
        this.dashboardTableRows = rows;
      }, 0);
    }
  }

  get filteredAirCarrierLicences(): CrmAirCarrierLicence[] {
    const search = this.airCarrierLicenceSearchTerm.trim().toLowerCase();
    return this.airCarrierLicences
      .filter((licence) => !this.airCarrierLicenceCanadianOnly || (licence.nationalityCode || '').trim().toUpperCase() === 'CA')
      .filter((licence) => !search || this.getAirCarrierLicenceSearchTerms(licence).some((value) => value.toLowerCase().includes(search)));
  }

  get paginatedAirCarrierLicences(): CrmAirCarrierLicence[] {
    const startIndex = (this.currentAirCarrierLicencePage - 1) * this.airCarrierLicenceItemsPerPage;
    return this.filteredAirCarrierLicences.slice(startIndex, startIndex + this.airCarrierLicenceItemsPerPage);
  }

  get totalAirCarrierLicencePages(): number {
    return Math.max(1, Math.ceil(this.filteredAirCarrierLicences.length / this.airCarrierLicenceItemsPerPage));
  }

  get airCarrierLicenceRangeStart(): number {
    return this.filteredAirCarrierLicences.length === 0 ? 0 : (this.currentAirCarrierLicencePage - 1) * this.airCarrierLicenceItemsPerPage + 1;
  }

  get airCarrierLicenceRangeEnd(): number {
    return Math.min(this.currentAirCarrierLicencePage * this.airCarrierLicenceItemsPerPage, this.filteredAirCarrierLicences.length);
  }

  retryAirCarrierLicencesLoad(): void {
    this.loadAirCarrierLicences();
  }

  toggleAirCarrierLicencesSection(): void {
    this.isAirCarrierLicencesCollapsed = !this.isAirCarrierLicencesCollapsed;
  }

  getAirCarrierLicenceUrl(licence: CrmAirCarrierLicence): string {
    return `https://portail-portal.otc-cta.gc.ca/en/air-carrier-licences/${licence.licenceId}`;
  }

  onAirCarrierLicenceSearchChange(): void {
    this.currentAirCarrierLicencePage = 1;
  }

  onAirCarrierLicenceCanadianFilterChange(): void {
    this.currentAirCarrierLicencePage = 1;
  }

  convertAirCarrierLicenceToClient(licence: CrmAirCarrierLicence): void {
    this.openCreateClientModal();
    this.createClientForm = {
      ...this.createClientForm,
      clientName: licence.airCarrierName || licence.displayedAirCarrierNameE || '',
      legalName: licence.airCarrierName || '',
      industry: 'Aviation',
      addressCountry: (licence.nationalityCode || '').trim().toUpperCase() === 'CA' ? 'Canada' : (licence.nationalityNameE || ''),
      note: `Converted from Air Carrier Licence #${licence.licenceNum} (${licence.licenceTypeDescE}).`
    };
  }

  goToAirCarrierLicencePage(page: number): void {
    if (page < 1 || page > this.totalAirCarrierLicencePages) {
      return;
    }

    this.currentAirCarrierLicencePage = page;
  }

  trackByAirCarrierLicenceId(index: number, licence: CrmAirCarrierLicence): number {
    return licence.licenceId;
  }

  private loadAirCarrierLicences(): void {
    this.isLoadingAirCarrierLicences = true;
    this.airCarrierLicencesError = '';
    this.crmService.getAirCarrierLicences().subscribe(
      (licences) => {
        this.airCarrierLicences = this.dedupeAirCarrierLicencesByCarrierName(licences);
        this.currentAirCarrierLicencePage = 1;
        this.isLoadingAirCarrierLicences = false;
      },
      (error) => {
        this.airCarrierLicences = [];
        this.airCarrierLicencesError = this.requestErrorMessage(error, 'Unable to load Air Carrier Licences.');
        this.isLoadingAirCarrierLicences = false;
      }
    );
  }

  private dedupeAirCarrierLicencesByCarrierName(licences: CrmAirCarrierLicence[]): CrmAirCarrierLicence[] {
    const seenCarrierNames = new Set<string>();
    const uniqueLicences: CrmAirCarrierLicence[] = [];

    for (const licence of licences) {
      const key = (licence.airCarrierName || '').trim().toLowerCase();
      if (seenCarrierNames.has(key)) {
        continue;
      }

      seenCarrierNames.add(key);
      uniqueLicences.push(licence);
    }

    return uniqueLicences;
  }

  private getAirCarrierLicenceSearchTerms(licence: CrmAirCarrierLicence): string[] {
    return [
      licence.airCarrierName,
      licence.businessNames,
      licence.licenceNum,
      licence.licenceTypeDescE,
      licence.licenceStatusDescE
    ].filter((value): value is string => !!value);
  }

  resetDashboardFilters(): void {
    this.clientSearchTerm = '';
    this.contactSearchTerm = '';
    this.selectedVendorId = 'all';
    this.selectedType = 'all';    this.selectedStage = 'all';
    this.refreshDashboardTable();
  }

  updateDashboardTableRows(rows: CrmDashboardTableRow[]): void {
    const nextRows = Array.isArray(rows) ? rows : [];
    setTimeout(() => {
      this.dashboardTableRows = nextRows;
    }, 0);
  }

  onVendorFilterChange(selectedVendorId: string): void {
    this.selectedVendorId = selectedVendorId;
    this.refreshDashboardTable();
  }

  openContactModal(network: CrmNetwork): void {
    this.selectedContactNetwork = network;
    this.selectedContactEntries = this.getContactEntries(network.primary_contact);
    this.selectedContactDraft = this.buildContactDraft(network.primary_contact, network);
    this.contactModalError = '';
    this.isSavingContact = false;
  }

  closeContactModal(): void {
    if (this.isSavingContact) {
      return;
    }

    this.selectedContactNetwork = null;
    this.selectedContactEntries = [];
    this.selectedContactDraft = null;
    this.contactModalError = '';
  }

  openBillingModalFromContactModal(): void {
    if (this.isSavingContact || !this.selectedContactNetwork) {
      return;
    }

    const network = this.selectedContactNetwork;
    this.closeContactModal();
    this.openBillingModal(network);
  }

  openBillingModal(network: CrmNetwork): void {
    this.selectedBillingNetwork = network;
    this.selectedBillingDraft = this.buildBillingDraft(network.company_billing, network);
    this.billingModalError = '';
    this.isSavingBilling = false;
  }

  closeBillingModal(): void {
    if (this.isSavingBilling) {
      return;
    }

    this.selectedBillingNetwork = null;
    this.selectedBillingDraft = null;
    this.billingModalError = '';
  }

  openContactModalFromBillingModal(): void {
    if (this.isSavingBilling || !this.selectedBillingNetwork) {
      return;
    }

    const network = this.selectedBillingNetwork;
    this.closeBillingModal();
    this.openContactModal(network);
  }

  saveBillingInfo(): void {
    if (!this.selectedBillingNetwork || !this.selectedBillingDraft) {
      return;
    }

    const companyId = this.getCompanyId(this.selectedBillingNetwork);
    if (companyId === null) {
      this.billingModalError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const draft = this.selectedBillingDraft;
    const billingPayload: Partial<CrmCompanyBilling> = {
      billing_contact_name: this.nullIfEmpty(draft.billing_contact_name),
      billing_email: this.nullIfEmpty(draft.billing_email),
      billing_phone: this.nullIfEmpty(draft.billing_phone),
      billing_address_json: this.buildAddress(
        draft.billingStreet1,
        draft.billingStreet2,
        draft.billingCity,
        draft.billingRegion,
        draft.billingPostalCode,
        draft.billingCountry
      ),
      primary_registrar_email: this.nullIfEmpty(draft.primary_registrar_email),
      alternate_account_contact_json: this.buildAltenateAccountContact(
        draft.alternateAccountContactName,
        draft.alternateAccountContactEmail,
        draft.alternateAccountContactPhone
      ),
      billing_instructions: this.nullIfEmpty(draft.billing_instructions),
      last_pushed: this.nullIfEmpty(draft.last_pushed),
      last_pulled: this.nullIfEmpty(draft.last_pulled)
    };

    this.isSavingBilling = true;
    this.billingModalError = '';
    this.crmService.updateClient(companyId, { company_billing: billingPayload }).subscribe(
      (response) => {
        const savedBilling = response.client && response.client.company_billing
          ? response.client.company_billing
          : {
            ...this.selectedBillingNetwork!.company_billing,
            ...billingPayload,
            company_id: companyId
          } as CrmCompanyBilling;
        this.applyDashboardClientPatch(companyId, { company_billing: savedBilling });
        this.isSavingBilling = false;
        this.closeBillingModal();
      },
      (error) => {
        this.billingModalError = this.requestErrorMessage(error, 'Unable to save the billing info.');
        this.isSavingBilling = false;
      }
    );
  }

  savePrimaryContact(): void {
    if (!this.selectedContactNetwork || !this.selectedContactDraft) {
      return;
    }

    const companyId = this.getCompanyId(this.selectedContactNetwork);
    if (companyId === null) {
      this.contactModalError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const firstName = String(this.selectedContactDraft.first_name || '').trim();
    const lastName = String(this.selectedContactDraft.last_name || '').trim();
    const email = String(this.selectedContactDraft.email || '').trim();
    if (!firstName || !lastName || !email) {
      this.contactModalError = 'First name, last name, and email are required.';
      return;
    }

    const primaryContact = {
      ...this.selectedContactNetwork.primary_contact,
      ...this.selectedContactDraft,
      first_name: firstName,
      last_name: lastName,
      email,
      company_id: companyId,
      is_primary_for_company: true
    };

    this.isSavingContact = true;
    this.contactModalError = '';
    this.crmService.updateClient(companyId, { primary_contact: primaryContact }).subscribe(
      (response) => {
        const savedContact = response.client && response.client.primary_contact
          ? response.client.primary_contact
          : primaryContact as CrmContact;
        this.applyDashboardClientPatch(companyId, { primary_contact: savedContact });
        this.isSavingContact = false;
        this.closeContactModal();
      },
      (error) => {
        this.contactModalError = this.requestErrorMessage(error, 'Unable to save the primary contact.');
        this.isSavingContact = false;
      }
    );
  }

  onDashboardStageChange(network: CrmNetwork, stage: CrmNetwork['stage']): void {
    if (network.stage === stage) {
      return;
    }

    this.saveDashboardClientField(network, { stage }, { stage: network.stage }, 'stage');
  }

  onDashboardTypeChange(network: CrmNetwork, type: CrmNetwork['type']): void {
    if (network.type === type) {
      return;
    }

    this.saveDashboardClientField(network, { type }, { type: network.type }, 'type');
  }

  onDashboardTagListUpdated(row: CrmDashboardTableRow, update: CrmTagListUpdate): void {
    const network = row.network;
    const companyId = this.getCompanyId(network);
    if (companyId === null) {
      this.dashboardInlineError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const tagsCsv = this.tagItemsToCsv(update && update.final ? update.final : []);
    const previousTagsCsv = this.getClientTags(network).join(', ');
    if (tagsCsv === previousTagsCsv) {
      return;
    }

    this.dashboardInlineError = '';
    this.applyDashboardClientPatch(companyId, { tags_csv: tagsCsv });

    this.crmService.updateClient(companyId, { tags_csv: tagsCsv }).subscribe(
      (response) => {
        if (response.client) {
          this.applyDashboardClientPatch(companyId, {
            tags_csv: response.client.tags_csv
          });
        }
      },
      (error) => {
        this.applyDashboardClientPatch(companyId, { tags_csv: network.tags_csv });
        this.dashboardInlineError = this.requestErrorMessage(error, 'Unable to save the record tags.');
      }
    );
  }

  isSavingDashboardField(network: CrmNetwork, field: 'stage' | 'type'): boolean {
    return this.savingDashboardFields.has(this.getDashboardFieldKey(network, field));
  }

  getPrimaryContactName(network: CrmNetwork): string {
    const contact = network.primary_contact;
    return contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : '';
  }

  getPrimaryContactPreferredValue(network: CrmNetwork): string {
    const contact = network.primary_contact;
    if (!contact) {
      return '';
    }

    return this.getPreferredContactValue(
      contact.preferred_contact,
      contact.email || contact.email_alt || '',
      contact.phone || contact.phone_office || contact.phone_cell || contact.phone_alt || ''
    );
  }

  getPrimaryContactEmail(network: CrmNetwork): string {
    const contact = network.primary_contact;
    return contact ? (contact.email || contact.email_alt || '').trim() : '';
  }

  hasPrimaryContactInfo(network: CrmNetwork): boolean {
    return !!(this.getPrimaryContactName(network) || this.getPrimaryContactPreferredValue(network));
  }

  getBillingContactName(network: CrmNetwork): string {
    return (network.company_billing && network.company_billing.billing_contact_name) || '';
  }

  getBillingPreferredValue(network: CrmNetwork): string {
    const billing = network.company_billing;
    if (!billing) {
      return '';
    }

    const billingContact = this.getBillingContactRecord(network);
    const billingPreferredContact = (billing as CrmCompanyBilling & { preferred_contact?: string | null }).preferred_contact;
    return this.getPreferredContactValue(
      billingPreferredContact || (billingContact ? billingContact.preferred_contact : null),
      billing.billing_email || (billingContact ? billingContact.email || billingContact.email_alt || '' : ''),
      billing.billing_phone || (billingContact ? billingContact.phone || billingContact.phone_office || billingContact.phone_cell || billingContact.phone_alt || '' : '')
    );
  }

  getBillingContactEmail(network: CrmNetwork): string {
    const billing = network.company_billing;
    if (!billing) {
      return '';
    }

    const billingContact = this.getBillingContactRecord(network);
    return (billing.billing_email || (billingContact ? billingContact.email || billingContact.email_alt || '' : '')).trim();
  }

  hasBillingInfo(network: CrmNetwork): boolean {
    return !!(this.getBillingContactName(network) || this.getBillingPreferredValue(network));
  }

  hasSamePrimaryAndBillingContact(network: CrmNetwork): boolean {
    if (!this.hasPrimaryContactInfo(network) || !this.hasBillingInfo(network)) {
      return false;
    }

    const primaryName = this.normalizeContactMatchValue(this.getPrimaryContactName(network));
    const billingName = this.normalizeContactMatchValue(this.getBillingContactName(network));
    const primaryEmail = this.normalizeContactMatchValue(this.getPrimaryContactEmail(network));
    const billingEmail = this.normalizeContactMatchValue(this.getBillingContactEmail(network));

    return !!primaryName
      && !!billingName
      && !!primaryEmail
      && primaryName === billingName
      && primaryEmail === billingEmail;
  }

  openCreateClientModal(): void {
    this.createClientForm = this.getDefaultCreateClientForm();
    this.createClientSections = this.getDefaultCreateClientSections();
    this.lcSearchTerm = '';
    this.createClientError = '';
    this.activeCreateDropdown = null;
    this.primaryNetworkOptionsPage = 1;
    this.showCreateClientModal = true;
  }

  closeCreateClientModal(): void {
    this.showCreateClientModal = false;
    this.lcSearchTerm = '';
    this.createClientError = '';
    this.activeCreateDropdown = null;
    this.primaryNetworkOptionsPage = 1;
  }

  get filteredActionClientOptions(): CrmActionClientOption[] {
    const search = this.actionClientSearchTerm.trim().toLowerCase();

    return this.networks
      .map((network) => ({
        id: Number(network.company_id || network.id || 0),
        name: network.name || `Client #${network.company_id || network.id}`
      }))
      .filter((option) => option.id > 0 && (!search || option.name.toLowerCase().includes(search)))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  get canSaveDashboardAction(): boolean {
    return !this.isSavingClientAction
      && Number(this.actionModalClientId || 0) > 0
      && this.manualActionDraft.level.trim().length > 0
      && this.manualActionDraft.action.trim().length > 0
      && this.manualActionDraft.actionDate.trim().length > 0
      && this.manualActionDraft.dueDate.trim().length > 0;
  }

  openAddActionModal(): void {
    this.manualActionDraft = this.getDefaultManualActionDraft();
    this.actionModalClientId = null;
    this.actionClientSearchTerm = '';
    this.addActionError = '';
    this.showAddActionModal = true;
  }

  closeAddActionModal(): void {
    if (this.isSavingClientAction) {
      return;
    }

    this.showAddActionModal = false;
    this.addActionError = '';
    this.manualActionDraft = this.getDefaultManualActionDraft();
    this.actionModalClientId = null;
    this.actionClientSearchTerm = '';
  }

  saveClientAction(): void {
    if (!this.crmUser) {
      this.addActionError = 'CRM login is required before saving actions.';
      return;
    }

    const companyId = Number(this.actionModalClientId || 0);
    if (companyId <= 0) {
      this.addActionError = 'Select a client before saving this action.';
      return;
    }

    if (!this.canSaveDashboardAction) {
      this.addActionError = 'Client, level, action, details, action date, and due date are required.';
      return;
    }

    this.addActionError = '';
    this.isSavingClientAction = true;
    const payload = {
      level: this.manualActionDraft.level,
      action: this.manualActionDraft.action.trim(),
      details: this.manualActionDraft.details.trim(),
      action_date: this.manualActionDraft.dueDate.trim(),
      created: this.manualActionDraft.actionDate.trim()
    };

    this.crmService.createClientAction(companyId, payload).subscribe(
      () => {
        this.isSavingClientAction = false;
        this.showAddActionModal = false;
        this.manualActionDraft = this.getDefaultManualActionDraft();
        this.actionModalClientId = null;
        this.actionClientSearchTerm = '';
        this.refreshDashboardActionsData();
      },
      (error) => {
        this.isSavingClientAction = false;
        this.addActionError = this.requestErrorMessage(error, 'Unable to add action.');
      }
    );
  }

  trackByClientOption(index: number, option: CrmActionClientOption): number {
    return option.id;
  }

  trackByText(index: number, value: string): string {
    return value;
  }

  private refreshDashboardActionsData(): void {
    this.isLoadingDashboardActions = true;
    this.crmService.getDashboardActionsData().subscribe(
      (data) => {
        this.applyDashboardActionsData(data);
        this.isLoadingDashboardActions = false;
      },
      () => {
        this.isLoadingDashboardActions = false;
      }
    );
  }

  private getDefaultManualActionDraft(): CrmManualActionDraft {
    return {
      level: 'Medium',
      action: 'follow_up_phone',
      details: '',
      actionDate: this.getDateTimeInputValue(),
      dueDate: this.getDateTimeInputValue()
    };
  }

  private getDateTimeInputValue(date: Date = new Date()): string {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  toggleCreateClientSection(section: CrmCreateClientSectionKey): void {
    this.createClientSections = {
      ...this.createClientSections,
      [section]: !this.createClientSections[section]
    };
  }

  onCreateDropdownFocus(name: CrmCreateDropdownName): void {
    this.activeCreateDropdown = name;
  }

  onCreateDropdownInput(name: CrmCreateDropdownName): void {
    this.activeCreateDropdown = name;
  }

  onCreateDropdownBlur(name: CrmCreateDropdownName): void {
    window.setTimeout(() => {
      if (this.activeCreateDropdown === name) {
        this.activeCreateDropdown = null;
      }
    }, 120);
  }

  selectCreateDropdownItem(name: CrmCreateDropdownName, item: string): void {
    const fieldName = this.createDropdownFieldName(name);
    this.createClientForm[fieldName] = this.isTagDropdown(name)
      ? this.replaceCurrentCsvTag(this.createClientForm[fieldName], item)
      : item;
    this.activeCreateDropdown = null;
  }

  deleteCreateDropdownItem(name: CrmCreateDropdownName, item: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const deleteKey = this.dropdownDeleteKey(name, item);
    if (this.deletingCreateDropdownItems.has(deleteKey)) {
      return;
    }

    this.createClientError = '';
    this.deletingCreateDropdownItems.add(deleteKey);
    this.crmService.deleteDropdownItem(name, item).subscribe(
      (response) => {
        this.crmDropdowns = response.dropdowns;
        this.deletingCreateDropdownItems.delete(deleteKey);
      },
      (error) => {
        this.createClientError = this.requestErrorMessage(error, 'Unable to delete dropdown item.');
        this.deletingCreateDropdownItems.delete(deleteKey);
      }
    );
  }

  isDeletingCreateDropdownItem(name: CrmCreateDropdownName, item: string): boolean {
    return this.deletingCreateDropdownItems.has(this.dropdownDeleteKey(name, item));
  }

  getCreateDropdownSuggestions(name: CrmCreateDropdownName): string[] {
    const fieldName = this.createDropdownFieldName(name);
    const rawValue = String(this.createClientForm[fieldName] || '');
    const value = this.isTagDropdown(name)
      ? this.currentCsvTagSegment(rawValue).toLowerCase()
      : rawValue.trim().toLowerCase();
    const selectedTags = this.isTagDropdown(name)
      ? new Set(this.parseCsvList(rawValue).map((tag) => tag.toLowerCase()))
      : new Set<string>();
    const options = this.crmDropdowns[name] || [];
    const matches = value
      ? options.filter((option) => option.toLowerCase().includes(value))
      : options;

    return matches
      .filter((option) => !selectedTags.has(option.toLowerCase()))
      .slice(0, 8);
  }

  trackByDropdownOption(index: number, option: string): string {
    return `${index}:${option}`;
  }

  private dropdownDeleteKey(name: keyof CrmDropdownOptions, item: string): string {
    return `${name}:${String(item || '').trim().toLowerCase()}`;
  }

  private createDropdownFieldName(name: CrmCreateDropdownName): CrmCreateDropdownFieldName {
    switch (name) {
      case 'client_tags':
        return 'tagsCsv';
      case 'user_tags':
        return 'contactTagsCsv';
      case 'industry':
        return 'industry';
      case 'source':
      default:
        return 'source';
    }
  }

  private isTagDropdown(name: CrmCreateDropdownName): boolean {
    return name === 'client_tags' || name === 'user_tags';
  }

  private currentCsvTagSegment(value: string): string {
    const segments = String(value || '').split(',');
    return (segments.pop() || '').trim();
  }

  private replaceCurrentCsvTag(value: string, item: string): string {
    const segments = String(value || '').split(',');
    segments.pop();
    const tags = [
      ...segments.map((segment) => segment.trim()).filter(Boolean),
      item.trim()
    ];

    return Array.from(new Map(tags.map((tag) => [tag.toLowerCase(), tag])).values()).join(', ');
  }

  private getStoredDashboardViewMode(): CrmDashboardViewMode {
    try {
      const storedMode = window.localStorage.getItem(CRM_DASHBOARD_VIEW_MODE_STORAGE_KEY);
      return storedMode === 'simple' ? 'simple' : 'detailed';
    } catch (error) {
      return 'detailed';
    }
  }

  private getStoredDashboardFilters(): CrmStoredDashboardFilters {
    const defaults: CrmStoredDashboardFilters = {
      clientSearchTerm: '',
      contactSearchTerm: '',
      selectedVendorId: 'all',
      selectedType: 'all',
      selectedStage: 'all'
    };

    try {
      const stored = window.localStorage.getItem(CRM_DASHBOARD_FILTERS_STORAGE_KEY);
      if (!stored) {
        return defaults;
      }

      const parsed = JSON.parse(stored);
      return {
        clientSearchTerm: typeof parsed.clientSearchTerm === 'string' ? parsed.clientSearchTerm : defaults.clientSearchTerm,
        contactSearchTerm: typeof parsed.contactSearchTerm === 'string' ? parsed.contactSearchTerm : defaults.contactSearchTerm,
        selectedVendorId: typeof parsed.selectedVendorId === 'string' ? parsed.selectedVendorId : defaults.selectedVendorId,
        selectedType: typeof parsed.selectedType === 'string' ? parsed.selectedType : defaults.selectedType,
        selectedStage: typeof parsed.selectedStage === 'string' ? parsed.selectedStage : defaults.selectedStage
      };
    } catch (error) {
      return defaults;
    }
  }

  private saveDashboardFilters(): void {
    const filters: CrmStoredDashboardFilters = {
      clientSearchTerm: this.clientSearchTerm,
      contactSearchTerm: this.contactSearchTerm,
      selectedVendorId: this.selectedVendorId,
      selectedType: this.selectedType || 'all',
      selectedStage: this.selectedStage || 'all'
    };

    try {
      window.localStorage.setItem(CRM_DASHBOARD_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      // Ignore localStorage failures; filters still apply for this session.
    }
  }

  private buildDashboardViewTabs(mode: CrmDashboardViewMode): TabsNavigationItems[] {
    return [
      { name: 'Detailed View', icon: 'fa-expand', selected: mode === 'detailed' },
      { name: 'Simple View', icon: 'fa-compress', selected: mode === 'simple' }
    ];
  }

  isLinkedLcSelected(networkId: number): boolean {
    return this.createClientForm.linkedNetworkIds.includes(networkId);
  }

  isPrimaryLcSelected(networkId: number): boolean {
    return this.createClientForm.primaryNetworkId === networkId;
  }

  onLinkedLcToggle(networkId: number, checked: boolean): void {
    const currentIds = new Set(this.createClientForm.linkedNetworkIds);

    if (checked) {
      currentIds.add(networkId);
    } else {
      currentIds.delete(networkId);
    }

    const linkedNetworkIds = Array.from(currentIds);
    let primaryNetworkId = this.createClientForm.primaryNetworkId;

    if (checked && (primaryNetworkId === null || !linkedNetworkIds.includes(primaryNetworkId))) {
      primaryNetworkId = networkId;
    }

    if (!checked && primaryNetworkId === networkId) {
      primaryNetworkId = linkedNetworkIds.length > 0 ? linkedNetworkIds[0] : null;
    }

    this.createClientForm = {
      ...this.createClientForm,
      linkedNetworkIds,
      primaryNetworkId
    };
  }

  onPrimaryLcSelect(networkId: number): void {
    if (!this.isLinkedLcSelected(networkId)) {
      return;
    }

    this.createClientForm = {
      ...this.createClientForm,
      primaryNetworkId: networkId
    };
  }

  onNetworkOptionControlsClick(networkId: number, event: MouseEvent): void {
    event.stopPropagation();

    if (!this.isLinkedLcSelected(networkId)) {
      this.onLinkedLcToggle(networkId, true);
    }
  }

  onPrimaryLcControlClick(networkId: number, event: MouseEvent): void {
    event.stopPropagation();

    if (!this.isLinkedLcSelected(networkId)) {
      this.onLinkedLcToggle(networkId, true);
      this.onPrimaryLcSelect(networkId);
    }
  }

  toggleLcSelection(networkId: number): void {
    this.onLinkedLcToggle(networkId, !this.isLinkedLcSelected(networkId));
  }

  onLcSearchChange(): void {
    this.primaryNetworkOptionsPage = 1;
  }

  goToPrimaryNetworkOptionsPage(page: number): void {
    const totalPages = this.totalPrimaryNetworkOptionPages;

    if (totalPages === 0) {
      this.primaryNetworkOptionsPage = 1;
      return;
    }

    this.primaryNetworkOptionsPage = Math.max(1, Math.min(page, totalPages));
  }

  onTokenModalFilterChange(): void {
    this.tokenModalPage = 1;
    this.refreshTokenModalRows();
  }

  resetTokenModalFilters(): void {
    this.tokenModalLcSearchTerm = '';
    this.tokenModalTokenTypeSearchTerm = '';
    this.tokenModalPage = 1;
    this.refreshTokenModalRows();
  }

  goToTokenModalPage(page: number): void {
    const totalPages = this.totalTokenModalPages;

    if (totalPages === 0) {
      this.tokenModalPage = 1;
      this.refreshTokenModalRows();
      return;
    }

    this.tokenModalPage = Math.max(1, Math.min(page, totalPages));
    this.refreshTokenModalRows();
  }

  onRecordModalFilterChange(): void {
    this.recordModalPage = 1;
    this.refreshRecordModalRows();
  }

  resetRecordModalFilters(refresh = true): void {
    this.recordModalRecordSearchTerm = '';
    this.recordModalLcSearchTerm = '';
    this.recordModalStageFilter = 'all';
    this.recordModalTypeFilter = 'all';
    this.recordModalPage = 1;

    if (refresh) {
      this.refreshRecordModalRows();
      return;
    }

    this.filteredRecordModalRows = [];
    this.paginatedRecordModalRows = [];
    this.currentRecordModalPage = 1;
    this.totalRecordModalPages = 0;
    this.recordModalRangeStart = 0;
    this.recordModalRangeEnd = 0;
  }

  goToRecordModalPage(page: number): void {
    const totalPages = this.totalRecordModalPages;

    if (totalPages === 0) {
      this.recordModalPage = 1;
      return;
    }

    const nextPage = Math.max(1, Math.min(page, totalPages));
    if (nextPage === this.recordModalPage) {
      return;
    }

    this.recordModalPage = nextPage;
    this.refreshRecordModalRows();
  }

  onRenewalModalFilterChange(): void {
    this.renewalModalPage = 1;
    this.refreshRenewalModalRows();
  }

  sortRenewalModalByPaymentDue(): void {
    this.renewalModalSortDirection = this.renewalModalSortDirection === 'asc' ? 'desc' : 'asc';
    this.renewalModalPage = 1;
    this.refreshRenewalModalRows();
  }

  getRenewalModalSortIndicator(): string {
    return this.renewalModalSortDirection ? this.renewalModalSortDirection.toUpperCase() : '';
  }

  resetRenewalModalFilters(refresh = true): void {
    this.renewalModalLcSearchTerm = '';
    this.renewalModalSortDirection = null;
    this.renewalModalPage = 1;

    if (refresh) {
      this.refreshRenewalModalRows();
      return;
    }

    this.filteredRenewalModalRows = [];
    this.paginatedRenewalModalRows = [];
    this.currentRenewalModalPage = 1;
    this.totalRenewalModalPages = 0;
    this.renewalModalRangeStart = 0;
    this.renewalModalRangeEnd = 0;
  }

  goToRenewalModalPage(page: number): void {
    const totalPages = this.totalRenewalModalPages;

    if (totalPages === 0) {
      this.renewalModalPage = 1;
      return;
    }

    const nextPage = Math.max(1, Math.min(page, totalPages));
    if (nextPage === this.renewalModalPage) {
      return;
    }

    this.renewalModalPage = nextPage;
    this.refreshRenewalModalRows();
  }

  onActionModalFilterChange(): void {
    if (this.actionModalIssueFilter !== 'all' && !this.actionModalIssueOptions.includes(this.actionModalIssueFilter)) {
      this.actionModalIssueFilter = 'all';
    }

    this.actionModalPage = 1;
    this.refreshActionModalRows();
  }

  toggleActionSeverityMenu(event: Event): void {
    event.stopPropagation();
    this.isActionSeverityMenuOpen = !this.isActionSeverityMenuOpen;
  }

  selectActionSeverityFilter(severity: CrmActionModalSeverityFilter, event: Event): void {
    event.stopPropagation();
    this.actionModalSeverityFilter = severity;
    this.isActionSeverityMenuOpen = false;
    this.onActionModalFilterChange();
  }

  resetActionModalFilters(): void {
    this.actionModalSeverityFilter = 'all';
    this.actionModalIssueFilter = 'all';
    this.actionModalLcSearchTerm = '';
    this.actionModalHasDueDateOnly = false;
    this.isActionSeverityMenuOpen = false;
    this.actionModalSortColumn = null;
    this.actionModalSortDirection = 'asc';
    this.actionModalPage = 1;
    this.refreshActionModalRows();
  }

  sortActionModal(column: CrmActionModalSortColumn): void {
    if (this.actionModalSortColumn === column) {
      this.actionModalSortDirection = this.actionModalSortDirection === 'asc' ? 'desc' : 'asc';
      this.actionModalPage = 1;
      this.refreshActionModalRows();
      return;
    }

    this.actionModalSortColumn = column;
    this.actionModalSortDirection = 'asc';
    this.actionModalPage = 1;
    this.refreshActionModalRows();
  }

  getActionModalSortIndicator(column: CrmActionModalSortColumn): string {
    if (this.actionModalSortColumn !== column) {
      return '';
    }

    return this.actionModalSortDirection.toUpperCase();
  }

  formatIssueLabel(issue: string): string {
    if (!issue) {
      return issue;
    }

    return issue
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatBreakdownLabel(value: string): string {
    return value
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  getActionDeadlineClass(deadline: string | null): string {
    if (!deadline) {
      return '';
    }

    const deadlineDate = new Date(deadline);
    if (Number.isNaN(deadlineDate.getTime())) {
      return 'deadline-pill-red';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadlineDate.setHours(0, 0, 0, 0);
    const daysUntilDeadline = Math.floor((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilDeadline <= 0) {
      return 'deadline-pill-red';
    }

    if (daysUntilDeadline <= 7) {
      return 'deadline-pill-orange';
    }

    return 'deadline-pill-green';
  }

  goToActionModalPage(page: number): void {
    const totalPages = this.totalActionModalPages;

    if (totalPages === 0) {
      this.actionModalPage = 1;
      return;
    }

    const nextPage = Math.max(1, Math.min(page, totalPages));
    if (nextPage === this.actionModalPage) {
      return;
    }

    this.actionModalPage = nextPage;
    this.refreshActionModalRows();
  }

  closeActionSeverityMenu(): void {
    this.isActionSeverityMenuOpen = false;
  }

  createClient(): void {
    const validationError = this.getCreateClientValidationError();

    if (validationError) {
      this.createClientError = validationError;
      return;
    }

    const nextLinkId = this.getNextLinkId();
    const nextCompanyId = this.getNextNetworkId();
    const selectedLinkedNetworks = this.createClientForm.linkedNetworkIds
      .map((networkId) => this.primaryNetworkOptions.find((option) => option.network_id === networkId))
      .filter((option): option is CrmPrimaryNetworkOption => !!option)
      .map((option, index) => this.createCompanyNetworkLink(
        option,
        option.network_id === this.createClientForm.primaryNetworkId,
        nextLinkId + index,
        nextCompanyId
      ))
      .sort((left, right) => Number(right.is_primary) - Number(left.is_primary));

    if (selectedLinkedNetworks.length !== this.createClientForm.linkedNetworkIds.length) {
      this.createClientError = 'One or more selected LCs are no longer available. Refresh the list and try again.';
      return;
    }

    const primaryNetwork = selectedLinkedNetworks.find((link) => link.is_primary) || null;
    const createdAt = new Date();
    const createdDateOnly = this.formatDateOnly(createdAt);
    const createdTimestamp = this.formatTimestamp(createdAt);
    const nextVendorId = 0;
    const nextContactId = this.getNextContactId();
    const nextBillingId = this.getNextBillingId();
    const nextReferralId = this.getNextReferralId();
    const notesSummary = this.createClientForm.note.trim() || `Manual CRM record entry created on ${createdDateOnly}.`;
    const companyAddress = this.buildAddress(
      this.createClientForm.addressStreet1,
      this.createClientForm.addressStreet2,
      this.createClientForm.addressCity,
      this.createClientForm.addressRegion,
      this.createClientForm.addressPostalCode,
      this.createClientForm.addressCountry
    );
    const billingAddress = this.buildAddress(
      this.createClientForm.billingStreet1,
      this.createClientForm.billingStreet2,
      this.createClientForm.billingCity,
      this.createClientForm.billingRegion,
      this.createClientForm.billingPostalCode,
      this.createClientForm.billingCountry
    );
    const mailingAddress = this.buildAddress(
      this.createClientForm.mailingStreet1,
      this.createClientForm.mailingStreet2,
      this.createClientForm.mailingCity,
      this.createClientForm.mailingRegion,
      this.createClientForm.mailingPostalCode,
      this.createClientForm.mailingCountry
    ) || companyAddress;
    const physicalAddress = this.buildAddress(
      this.createClientForm.physicalStreet1,
      this.createClientForm.physicalStreet2,
      this.createClientForm.physicalCity,
      this.createClientForm.physicalRegion,
      this.createClientForm.physicalPostalCode,
      this.createClientForm.physicalCountry
    ) || companyAddress;
    const converted = this.parseOptionalDateTime(this.createClientForm.converted)
      || (this.createClientForm.stage === 'client' ? createdTimestamp : null);
    const primaryContact = this.buildCreateClientPrimaryContact(nextContactId, nextVendorId, nextCompanyId, createdTimestamp);
    const referralRecord = this.buildReferralRecord(nextReferralId, nextCompanyId, createdTimestamp);

    const newClient: CrmNetwork = {
      id: nextCompanyId,
      vendor_id: nextVendorId,
      name: this.createClientForm.clientName.trim(),
      legal_name: this.nullIfEmpty(this.createClientForm.legalName),
      client_note: this.nullIfEmpty(this.createClientForm.clientNote),
      stage: this.createClientForm.stage,
      type: this.createClientForm.type,
      source: this.nullIfEmpty(this.createClientForm.source),
      industry: this.nullIfEmpty(this.createClientForm.industry),
      website: this.nullIfEmpty(this.createClientForm.website),
      phone: this.nullIfEmpty(this.createClientForm.companyPhone),
      email_general: this.nullIfEmpty(this.createClientForm.companyEmail),
      address_json: companyAddress,
      mailing_address_json: mailingAddress,
      physical_address_json: physicalAddress,
      first_contact: this.parseOptionalDateTime(this.createClientForm.firstContact) || createdTimestamp,
      last_contact: this.parseOptionalDateTime(this.createClientForm.lastContact) || createdTimestamp,
      due_date: this.parseOptionalDateTime(this.createClientForm.dueDate),
      due_action: this.nullIfEmpty(this.createClientForm.dueAction),
      invoice_reference: this.nullIfEmpty(this.createClientForm.invoiceReference),
      support_ticket_reference: this.nullIfEmpty(this.createClientForm.supportTicketReference),
      contact_type_keys: [...this.createClientForm.contactTypeKeys],
      contact_type_other: this.nullIfEmpty(this.createClientForm.contactTypeOther),
      department_keys: [...this.createClientForm.departmentKeys],
      reference_only_note: null,
      notes_summary: notesSummary,
      tags_csv: this.nullIfEmpty(this.createClientForm.tagsCsv),
      owner_user_id: this.parseOptionalNumber(this.createClientForm.ownerUserId),
      converted,
      created: createdTimestamp,
      modified: createdTimestamp,
      deleted: null,
      primary_contact: primaryContact,
      primary_network: primaryNetwork,
      linked_networks: selectedLinkedNetworks,
      company_billing: {
        id: nextBillingId,
        company_id: nextCompanyId,
        billing_contact_name: this.nullIfEmpty(this.getCreateClientBillingContactName()),
        billing_email: this.nullIfEmpty(this.createClientForm.billingEmail),
        billing_phone: this.nullIfEmpty(this.createClientForm.billingPhone),
        billing_address_json: billingAddress,
        primary_registrar_email: this.nullIfEmpty(this.createClientForm.primaryRegistrarEmail),
        alternate_account_contact_json: this.buildAltenateAccountContact(
          this.createClientForm.alternateAccountContactName,
          this.createClientForm.alternateAccountContactEmail,
          this.createClientForm.alternateAccountContactPhone
        ),
        billing_instructions: this.nullIfEmpty(this.createClientForm.billingInstructions),
        last_pushed: null,
        last_pulled: null,
        created: createdTimestamp,
        modified: createdTimestamp
      },
      referral_record: referralRecord
    };

    this.crmService.createClient(newClient).subscribe(
      () => {
        this.loadDashboardData();
        this.closeCreateClientModal();
      },
      () => {
        this.createClientError = 'Unable to create the CRM record.';
      }
    );
  }

  trackByDashboardRowId(index: number, row: CrmDashboardTableRow): number {
    const network = row.network;
    return network.networkID || network.network_id || network.id || index;
  }

  trackByContactEntry(index: number, field: { label: string; value: string }): string {
    return `${index}:${field.label}`;
  }

  trackByBreakdownKey(index: number, item: CrmBreakdownItem): string {
    return item.key;
  }

  trackByTokenLcSummaryId(index: number, item: CrmDashboardTokenLcSummary): number {
    return item.networkID || index;
  }

  trackByTokenRoleId(index: number, item: CrmDashboardTokenRoleSummary): number {
    return item.roleId || index;
  }

  trackByTokenModalRowKey(index: number, item: CrmTokenModalRow): string {
    return item.key || String(index);
  }

  trackByTokenRequestId(index: number, item: CrmDashboardTokenRequestHistory): number {
    return item.requestID || index;
  }

  trackByActionIssue(_index: number, item: CrmDashboardActionIssue): string {
    return `${item.severity}:${item.lcLabel}:${item.issue}:${item.details}`;
  }

  trackByActionSeveritySummary(_index: number, item: CrmActionSeveritySummary): string {
    return item.severity;
  }

  trackByMetricModalRow(index: number, row: string[]): string {
    return `${index}:${row.join('|')}`;
  }

  trackByVendorFilterValue(index: number, item: CrmVendorFilterOption): string {
    return item.value || String(index);
  }

  trackByPrimaryNetworkId(index: number, option: CrmPrimaryNetworkOption): number {
    return option.network_id;
  }


  onPrintMenuSelect(optionId: string): void {
    switch (optionId) {
      case 'dashboard-report':
        this.printDashboardReport();
        break;
      case 'client-records-report':
        this.printClientRecordsReport();
        break;
      default:
        break;
    }
  }

  private printDashboardReport(): void {
    const networks = this.filteredNetworks;
    const sections = [
      renderPrintSection(
        'Report Snapshot',
        renderFieldGrid([
          { label: 'Records Shown', value: networks.length },
          { label: 'Total Records', value: this.totalCompanyCount },
          { label: 'Vendor LCs', value: this.vendorLcCount },
          { label: 'Upcoming Invoice Due', value: this.renewalCompanyCount },
          { label: 'Tokens Total', value: `${this.tokenTotalCount} (${this.tokenCardBreakdown})` },
          { label: 'Action Items', value: this.actionItemCount },
          { label: 'Active Filters', value: this.getDashboardPrintFilterSummary(), fullWidth: true }
        ]),
        'Full-page CRM dashboard snapshot for the current filtered view.'
      ),
      renderPrintSection(
        'Metric Cards',
        renderMetricGrid(this.buildDashboardMetricCards()),
        'All dashboard metric-card components.'
      ),
      renderPrintSection(
        'CRM Insights',
        renderFieldCards(this.buildDashboardInsightCards(), 'No CRM insights are available.'),
        'Breakdowns from the crm-insights-grid.'
      ),
      renderPrintSection(
        'CRM Records',
        renderTable(
          [
            { label: 'Record', width: '14%' },
            { label: 'CRM Stage', width: '8%' },
            { label: 'CRM Type', width: '8%' },
            { label: 'Primary Contact', width: '15%' },
            { label: 'Vendor', width: '18%' },
            { label: 'Record Note', width: '20%' },
            { label: 'Created', width: '9%', nowrap: true },
            { label: 'Updated', width: '9%', nowrap: true }
          ],
          this.buildDashboardClientTableRows(networks),
          'No records match the current dashboard filters.'
        ),
        'CRM records matching the active dashboard filters.'
      )
    ];

    printCrmReport({
      title: 'CRM Records Dashboard Report',
      subtitle: 'Printable dashboard snapshot, metrics, insights, and records.',
      meta: [`${networks.length} records shown`],
      orientation: 'landscape',
      sections
    });
  }

  private printClientRecordsReport(): void {
    const networks = this.filteredNetworks;
    const contacts = networks
      .map((network) => network.primary_contact)
      .filter((contact): contact is CrmContact => !!contact);
    const sections = [
      renderPrintSection(
        'CRM Records Extended View',
        renderTable(
          [
            { label: 'Record', width: '20%' },
            { label: 'Primary Contact', width: '32%' },
            { label: 'Vendor Summary', width: '16%' },
            { label: 'Record Note', width: '16%' },
            { label: 'Created', width: '8%', nowrap: true },
            { label: 'Updated', width: '8%', nowrap: true }
          ],
          this.buildClientExtendedTableRows(networks),
          'No records are available for printing.'
        ),
        'Extended CRM records with the primary contact attached to each row.',
        true
      ),
      renderPrintSection(
        'Contact Breakdown',
        renderFieldGrid([
          { label: 'Records Shown', value: networks.length },
          { label: 'Primary Contacts', value: contacts.length },
          { label: 'Contacts With Email', value: contacts.filter((contact) => !!contact.email).length },
          { label: 'Contacts With Phone', value: contacts.filter((contact) => !!contact.phone).length },
          { label: 'Contacts With Altenate Email', value: contacts.filter((contact) => !!contact.email_alt).length },
          { label: 'Contacts With Altenate Phone', value: contacts.filter((contact) => !!contact.phone_alt).length }
        ]),
        'Summary breakdown for the primary contact data in the current result set.'
      )
    ];

    printCrmReport({
      title: 'CRM Records Report',
      subtitle: 'Printable records report with extended contact views.',
      meta: [`${networks.length} records`, `${contacts.length} primary contacts`],
      orientation: 'landscape',
      sections
    });
  }

  private buildDashboardMetricCards() {
    return [
      { label: 'Total Records', value: this.totalCompanyCount },
      { label: 'Vendor LCs', value: this.vendorLcCount },
      { label: 'Upcoming Invoice Due', value: this.renewalCompanyCount },
      { label: 'Tokens Total', value: `${this.tokenTotalCount} (${this.tokenCardBreakdown})` },
      { label: 'Action Items', value: this.actionItemCount }
    ];
  }

  private buildDashboardInsightCards() {
    return [
      {
        title: 'Record Types',
        fields: this.companyTypeBreakdown.map((item) => ({ label: item.label, value: item.count }))
      },
      {
        title: 'Record Stages',
        fields: this.companyStageBreakdown.map((item) => ({ label: item.label, value: item.count }))
      },
      {
        title: 'LC Edition',
        fields: this.lcStatusBreakdown.map((item) => ({ label: item.label, value: item.count }))
      }
    ];
  }

  private buildDashboardClientTableRows(networks: CrmNetwork[]): unknown[][] {
    return networks.map((network) => {
      const primaryContact = network.primary_contact;
      const contactName = primaryContact
        ? `${primaryContact.first_name} ${primaryContact.last_name}`.trim()
        : '-';

      return [
        [this.getClientDisplayName(network), this.hasClientAttachedLc(network) ? `LC #${this.getNetworkId(network)}` : 'No LC'],
        this.formatBreakdownLabel(network.stage),
        network.type ? this.formatBreakdownLabel(network.type) : '-',
        [contactName, primaryContact ? (primaryContact.email || '-') : '-'],
        this.getVendorSummary(network),
        this.getClientNote(network) || '-',
        formatPrintDate(network.created),
        formatPrintDate(network.modified)
      ];
    });
  }

  private buildClientExtendedTableRows(networks: CrmNetwork[]): unknown[][] {
    return networks.map((network) => {
      const primaryContact = network.primary_contact;
      const contactName = primaryContact
        ? `${primaryContact.first_name} ${primaryContact.last_name}`.trim()
        : 'No primary contact assigned';
      const vendorId = this.getVendorId(network);
      const vendorLabel = network.vendorUnknown
        ? 'No vendor'
        : (vendorId !== null && vendorId > 0 ? `Vendor: ${this.getVendorSummary(network)}` : 'No vendor assigned');

      return [
        [
          this.getClientDisplayName(network),
          this.hasClientAttachedLc(network) ? `LC #${this.getNetworkId(network)}` : 'No LC',
          `Stage: ${this.formatBreakdownLabel(network.stage)}`,
          `Type: ${network.type ? this.formatBreakdownLabel(network.type) : '-'}`
        ],
        primaryContact
          ? [
              contactName,
              `Title: ${primaryContact.title || '-'}`,
              `Email: ${primaryContact.email || '-'}`,
              `Alt Email: ${primaryContact.email_alt || '-'}`,
              `Phone: ${primaryContact.phone || '-'}`,
              `Alt Phone: ${primaryContact.phone_alt || '-'}`,
              `Preferred: ${primaryContact.preferred_contact || '-'}`,
              `Roles: ${primaryContact.role.length > 0 ? primaryContact.role.join(', ') : '-'}`
            ]
          : [contactName],
        [
          network.vendor ? 'Vendor LC' : 'Standard LC',
          vendorLabel
        ],
        this.getClientNote(network) || '-',
        formatPrintDate(network.created),
        formatPrintDate(network.modified)
      ];
    });
  }

  private getDashboardPrintFilterSummary(): string[] {
    return [
      `Company: ${this.clientSearchTerm.trim() || 'All'}`,
      `Contact: ${this.contactSearchTerm.trim() || 'All'}`,
      `Vendor: ${this.getSelectedVendorFilterLabel()}`,
      `CRM type: ${this.selectedType === 'all' ? 'All' : this.formatBreakdownLabel(this.selectedType!)}`,
      `CRM stage: ${this.selectedStage === 'all' ? 'All' : this.formatBreakdownLabel(this.selectedStage)}`
    ];
  }

  private getSelectedVendorFilterLabel(): string {
    if (this.selectedVendorId === 'all') {
      return 'All';
    }

    const selectedOption = this.vendorFilterOptions.find((option) => option.value === this.selectedVendorId);
    return selectedOption ? selectedOption.label : 'All';
  }

  private loadDashboardData(): void {
    if (!this.crmUser) {
      this.isLoadingDashboard = false;
      return;
    }

    this.isLoadingDashboard = true;
    this.setDashboardSupplementalLoading(false);
    this.dashboardEnrichmentRequestId++;
    this.dashboardError = '';

    this.crmService.getDashboardData(true).subscribe(
      (data) => {
        this.applyDashboardData(data);
        this.isLoadingDashboard = false;
        this.loadDashboardSupplementalData();
        this.loadAirCarrierLicences();
      },
      (error) => {
        this.networks = [];
        this.unlinkedCompanies = [];
        this.actionQueue = [];
        this.vendorLcSummary = [];
        this.vendorLcOwners = [];
        this.tokenSummary = EMPTY_TOKEN_SUMMARY;
        this.supportSummary = [];
        this.quickbooksSummary = [];
        this.marketingSummary = [];
        this.adminSummary = [];
        this.actionModalIssues = [];
        this.actionModalSeveritySummaries = this.buildActionSeveritySummaries([]);
        this.actionModalIssueOptions = [];
        this.refreshActionModalRows();
        this.dashboardTableRows = [];
        this.dashboardTableData$.next([]);
        this.dashboardError = this.requestErrorMessage(error, 'Error loading CRM dashboard.');
        this.isLoadingDashboard = false;
        this.setDashboardSupplementalLoading(false);
      }
    );
  }

  private loadDashboardSupplementalData(): void {
    const requestId = ++this.dashboardEnrichmentRequestId;
    this.setDashboardSupplementalLoading(true);
    let pendingRequests = 5;
    const finishRequest = (requestType: CrmDashboardSupplementalRequest) => {
      if (requestId !== this.dashboardEnrichmentRequestId) {
        return;
      }

      this.setDashboardSupplementalRequestLoading(requestType, false);
      pendingRequests--;
      if (pendingRequests <= 0) {
        this.isLoadingDashboardEnrichment = false;
      }
    };

    this.crmService.getDashboardOptionsData().subscribe(
      (data) => {
        if (requestId === this.dashboardEnrichmentRequestId) {
          this.applyDashboardOptionsData(data);
        }
        finishRequest('options');
      },
      () => finishRequest('options')
    );

    this.crmService.getDashboardRecordDetailsData().subscribe(
      (data) => {
        if (requestId === this.dashboardEnrichmentRequestId) {
          this.applyDashboardRecordDetailsData(data);
        }
        finishRequest('recordDetails');
      },
      () => finishRequest('recordDetails')
    );

    this.crmService.getDashboardActionsData().subscribe(
      (data) => {
        if (requestId === this.dashboardEnrichmentRequestId) {
          this.applyDashboardActionsData(data);
        }
        finishRequest('actions');
      },
      () => finishRequest('actions')
    );

    this.crmService.getDashboardVendorsData().subscribe(
      (data) => {
        if (requestId === this.dashboardEnrichmentRequestId) {
          this.applyDashboardVendorsData(data);
        }
        finishRequest('vendors');
      },
      () => finishRequest('vendors')
    );

    this.crmService.getDashboardEnrichmentData().subscribe(
      (data) => {
        if (requestId === this.dashboardEnrichmentRequestId) {
          this.applyDashboardEnrichmentData(data);
        }
        finishRequest('tokens');
      },
      () => finishRequest('tokens')
    );
  }

  private setDashboardSupplementalLoading(isLoading: boolean): void {
    this.isLoadingDashboardEnrichment = isLoading;
    this.isLoadingDashboardOptions = isLoading;
    this.isLoadingDashboardRecordDetails = isLoading;
    this.isLoadingDashboardActions = isLoading;
    this.isLoadingDashboardVendors = isLoading;
    this.isLoadingDashboardTokens = isLoading;
  }

  private setDashboardSupplementalRequestLoading(requestType: CrmDashboardSupplementalRequest, isLoading: boolean): void {
    switch (requestType) {
      case 'options':
        this.isLoadingDashboardOptions = isLoading;
        break;
      case 'recordDetails':
        this.isLoadingDashboardRecordDetails = isLoading;
        break;
      case 'actions':
        this.isLoadingDashboardActions = isLoading;
        break;
      case 'vendors':
        this.isLoadingDashboardVendors = isLoading;
        break;
      case 'tokens':
        this.isLoadingDashboardTokens = isLoading;
        break;
    }
  }

  private initializeCrmSession(): void {
    this.crmUser = this.crmService.getStoredCrmUser();
    if (!this.crmUser) {
      this.isLoadingDashboard = false;
      return;
    }

    this.loadDashboardData();
  }

  private applyDashboardData(data: CrmDashboardData): void {
    this.networks = data.lcs || data.networks || [];
    this.unlinkedCompanies = data.unlinkedCompanies;
    this.actionQueue = data.actionQueue || [];
    this.vendorLcSummary = data.vendorLcSummary || [];
    this.vendorLcOwners = data.vendorLcOwners || [];
    this.crmDropdowns = data.dropdowns || { source: [], industry: [], client_tags: [], user_tags: [] };
    this.vendorFilterOptions = this.buildVendorFilterOptions(this.networks);
    if (this.selectedVendorId !== 'all' && !this.vendorFilterOptions.some((option) => option.value === this.selectedVendorId)) {
      this.selectedVendorId = 'all';
    }
    this.tokenSummary = data.tokenSummary || EMPTY_TOKEN_SUMMARY;
    this.supportSummary = data.supportSummary || [];
    this.quickbooksSummary = data.quickbooksSummary || [];
    this.marketingSummary = data.marketingSummary || [];
    this.adminSummary = data.adminSummary || [];
    this.rebuildActionModalIssues();
    this.applyLivePrimaryNetworkOptions(data.availablePrimaryNetworks || []);
    this.refreshDashboardTable();
  }

  private applyDashboardEnrichmentData(data: Partial<CrmDashboardData>): void {
    if (data.tokenSummary) {
      this.tokenSummary = data.tokenSummary;
    }

    this.rebuildActionModalIssues();
  }

  private applyDashboardOptionsData(data: Partial<CrmDashboardData>): void {
    if (data.dropdowns) {
      this.crmDropdowns = data.dropdowns;
    }
    this.applyLivePrimaryNetworkOptions(data.availablePrimaryNetworks || []);
  }

  private applyDashboardRecordDetailsData(data: Partial<CrmDashboardData>): void {
    const rows = data.lcs || data.networks || [];
    if (rows.length === 0) {
      return;
    }

    const detailRows = new Map(rows.map((row) => [this.getDashboardRowKey(row), row]));
    const mergedKeys = new Set<string>();
    this.networks = this.networks.map((network) => {
      const key = this.getDashboardRowKey(network);
      const detail = detailRows.get(key);
      if (!detail) {
        return network;
      }

      mergedKeys.add(key);
      return {
        ...network,
        ...detail
      };
    });
    rows.forEach((row) => {
      const key = this.getDashboardRowKey(row);
      if (!mergedKeys.has(key)) {
        this.networks.push(row);
      }
    });

    this.vendorFilterOptions = this.buildVendorFilterOptions(this.networks);
    this.rebuildActionModalIssues();
    this.refreshDashboardTable();
  }

  private getDashboardRowKey(network: CrmNetwork): string {
    const networkId = Number(network.networkID || network.network_id || 0);
    if (networkId > 0) {
      return `network:${networkId}`;
    }

    return `company:${Number(network.company_id || network.id || 0)}`;
  }

  private applyDashboardActionsData(data: Partial<CrmDashboardData>): void {
    this.actionQueue = data.actionQueue || [];
    this.rebuildActionModalIssues();
  }

  private applyDashboardVendorsData(data: Partial<CrmDashboardData>): void {
    this.vendorLcSummary = data.vendorLcSummary || [];
    this.vendorLcOwners = data.vendorLcOwners || [];
  }

  private loadLivePrimaryNetworkOptions(): void {
    const requestId = ++this.primaryNetworkOptionsRequestId;
    this.isLoadingPrimaryNetworkOptions = true;
    this.primaryNetworkOptionsError = "";

    this.crmService.getAvailablePrimaryNetworks().subscribe(
      (options) => {
        if (requestId !== this.primaryNetworkOptionsRequestId) {
          return;
        }

        this.applyLivePrimaryNetworkOptions(options);
      },
      () => {
        if (requestId === this.primaryNetworkOptionsRequestId) {
          this.handlePrimaryNetworkOptionsError("Error loading mock LCs.");
        }
      }
    );
  }

  private applyLivePrimaryNetworkOptions(options: CrmPrimaryNetworkOption[]): void {
    this.livePrimaryNetworkOptions = Array.from(new Map(options.map((option) => [option.network_id, option])).values())
      .sort((left, right) => (left.network_name || "").localeCompare(right.network_name || ""));

    this.refreshPrimaryNetworkOptions();
    this.primaryNetworkOptionsPage = 1;
    this.isLoadingPrimaryNetworkOptions = false;
  }

  private refreshPrimaryNetworkOptions(): void {
    const usedNetworkIds = this.getUsedNetworkIds();

    this.primaryNetworkOptions = this.livePrimaryNetworkOptions
      .filter((option) => !usedNetworkIds.has(option.network_id))
      .map((option) => ({ ...option }));
  }

  private handlePrimaryNetworkOptionsError(message: string): void {
    this.livePrimaryNetworkOptions = [];
    this.primaryNetworkOptions = [];
    this.primaryNetworkOptionsError = message;
    this.primaryNetworkOptionsPage = 1;
    this.isLoadingPrimaryNetworkOptions = false;
  }

  private requestErrorMessage(error: any, fallback: string): string {
    return (error && error.error && (error.error.message || error.error.error)) || (error && error.message) || fallback;
  }

  private buildContactDraft(contact: CrmContact | null, network: CrmNetwork): Partial<CrmContact> {
    return {
      id: contact ? contact.id : undefined,
      vendor_id: contact ? contact.vendor_id : network.vendor_id,
      first_name: contact ? contact.first_name || '' : '',
      last_name: contact ? contact.last_name || '' : '',
      nick_name: contact ? contact.nick_name || '' : '',
      department: contact ? contact.department || '' : '',
      title: contact ? contact.title || '' : '',
      position: contact ? contact.position || '' : '',
      email: contact ? contact.email || '' : '',
      email_alt: contact ? contact.email_alt || '' : '',
      phone: contact ? contact.phone || '' : '',
      phone_office: contact ? contact.phone_office || '' : '',
      phone_extension: contact ? contact.phone_extension || '' : '',
      phone_cell: contact ? contact.phone_cell || '' : '',
      phone_other: contact ? contact.phone_other || '' : '',
      phone_other_note: contact ? contact.phone_other_note || '' : '',
      phone_alt: contact ? contact.phone_alt || '' : '',
      preferred_contact: this.normalizePreferredContact(contact ? contact.preferred_contact : null),
      contact_note: contact ? contact.contact_note || '' : '',
      user_id: contact ? contact.user_id : null,
      role: contact ? [...(contact.role || [])] : [],
      tags_csv: contact ? contact.tags_csv || '' : '',
      is_primary_for_company: true
    };
  }

  private buildBillingDraft(billing: CrmCompanyBilling | null, network: CrmNetwork): CrmBillingDraft {
    const address = billing && billing.billing_address_json ? billing.billing_address_json : null;
    const alternateContact = billing && billing.alternate_account_contact_json ? billing.alternate_account_contact_json : null;

    return {
      id: billing ? billing.id : null,
      company_id: billing ? billing.company_id : this.getCompanyId(network),
      billing_contact_name: billing && billing.billing_contact_name ? billing.billing_contact_name : '',
      billing_email: billing && billing.billing_email ? billing.billing_email : '',
      billing_phone: billing && billing.billing_phone ? billing.billing_phone : '',
      billingStreet1: address ? address.street_1 || '' : '',
      billingStreet2: address && address.street_2 ? address.street_2 : '',
      billingCity: address ? address.city || '' : '',
      billingRegion: address ? address.region || '' : '',
      billingPostalCode: address ? address.postal_code || '' : '',
      billingCountry: address ? address.country || '' : '',
      primary_registrar_email: billing && billing.primary_registrar_email ? billing.primary_registrar_email : '',
      alternateAccountContactName: alternateContact ? alternateContact.name || '' : '',
      alternateAccountContactEmail: alternateContact ? alternateContact.email || '' : '',
      alternateAccountContactPhone: alternateContact ? alternateContact.phone || '' : '',
      billing_instructions: billing && billing.billing_instructions ? billing.billing_instructions : '',
      last_pushed: billing && billing.last_pushed ? billing.last_pushed : '',
      last_pulled: billing && billing.last_pulled ? billing.last_pulled : '',
      created: billing && billing.created ? billing.created : '',
      modified: billing && billing.modified ? billing.modified : ''
    };
  }

  private saveDashboardClientField(
    network: CrmNetwork,
    patch: Partial<CrmNetwork>,
    rollback: Partial<CrmNetwork>,
    field: 'stage' | 'type'
  ): void {
    const companyId = this.getCompanyId(network);
    if (companyId === null) {
      this.dashboardInlineError = 'This record cannot be saved because it is missing a CRM company id.';
      return;
    }

    const fieldKey = this.getDashboardFieldKey(network, field);
    this.savingDashboardFields.add(fieldKey);
    this.dashboardInlineError = '';
    this.applyDashboardClientPatch(companyId, patch);

    this.crmService.updateClient(companyId, patch).subscribe(
      (response) => {
        if (response.client) {
          this.applyDashboardClientPatch(companyId, {
            stage: response.client.stage,
            type: response.client.type,
            primary_contact: response.client.primary_contact
          });
        }
        this.savingDashboardFields.delete(fieldKey);
      },
      (error) => {
        this.applyDashboardClientPatch(companyId, rollback);
        this.dashboardInlineError = this.requestErrorMessage(error, 'Unable to save the record field.');
        this.savingDashboardFields.delete(fieldKey);
      }
    );
  }

  private applyDashboardClientPatch(companyId: number, patch: Partial<CrmNetwork>): void {
    this.networks = this.networks.map((network) => {
      const networkCompanyId = this.getCompanyId(network);
      return networkCompanyId === companyId ? { ...network, ...patch } : network;
    });
    this.patchDashboardTableRows(companyId, patch);

    if (this.selectedContactNetwork && this.getCompanyId(this.selectedContactNetwork) === companyId) {
      this.selectedContactNetwork = { ...this.selectedContactNetwork, ...patch };
      this.selectedContactEntries = this.getContactEntries(this.selectedContactNetwork.primary_contact);
    }
  }

  private patchDashboardTableRows(companyId: number, patch: Partial<CrmNetwork>): void {
    const patchRow = (row: CrmDashboardTableRow): CrmDashboardTableRow => {
      const rowCompanyId = this.getCompanyId(row.network);
      if (rowCompanyId !== companyId) {
        return row;
      }

      return this.mapNetworkToTableRow({ ...row.network, ...patch });
    };

    this.dashboardTableRows = this.dashboardTableRows.map(patchRow);

    const currentTableRows = this.dashboardTableData$.getValue();
    currentTableRows.forEach((row, index) => {
      currentTableRows[index] = patchRow(row);
    });
  }

  private getDashboardFieldKey(network: CrmNetwork, field: 'stage' | 'type'): string {
    return `${this.getCompanyId(network) || this.getNetworkId(network) || network.id}:${field}`;
  }

  private getUsedNetworkIds(): Set<number> {
    return this.networks.reduce((usedNetworkIds, network) => {
      this.getAttachedLinkedNetworks(network).forEach((link) => usedNetworkIds.add(link.network_id));
      return usedNetworkIds;
    }, new Set<number>());
  }

  private mapNetworkToTableRow(network: CrmNetwork): CrmDashboardTableRow {
    const contactName = [
      this.getPrimaryContactName(network),
      this.getPrimaryContactPreferredValue(network),
      this.getBillingContactName(network),
      this.getBillingPreferredValue(network)
    ].filter((value) => !!value).join(' ');

    return {
      network,
      clientName: this.getClientDisplayName(network),
      stage: network.stage,
      type: network.type || '',
      contactName,
      vendorSummary: this.getVendorSummary(network),
      noteSummary: this.getClientNote(network) || '',
      tags: this.getClientTagItems(network),
      createdDate: network.created,
      modifiedDate: network.modified
    };
  }

  private getClientSearchTerms(network: CrmNetwork): string[] {
    return [
      this.getClientDisplayName(network)
    ];
  }

  private getMetricModalState(type: CrmMetricModalType): CrmMetricModalState {
    switch (type) {
      case 'lcs':
        return {
          type,
          title: 'Total Records',
          description: 'Every record currently included in the CRM dashboard.',
          emptyLabel: 'No CRM records are available.',
          headers: ['Record', 'LC', 'Stage', 'Type', 'Primary Contact'],
          rows: [
            ...this.lcSnapshots.map((lc) => this.mapLcSnapshotRow(lc)),
            ...this.noLcClientSnapshots.map((client) => this.mapNoLcClientRow(client))
          ]
        };
      case 'vendor':
        return {
          type,
          title: 'Vendor LCs',
          description: 'Vendors identified from the All CRM Records table using the same vendor mapping as the table filter.',
          emptyLabel: 'No vendors are identified in the CRM records table.',
          headers: ['Vendor', 'Records', 'LCs'],
          rows: this.getVendorModalSummaries().map((summary) => this.mapVendorModalSummaryRow(summary))
        };
      case 'renewal':
        this.renewalModalLcs = this.lcSnapshots.filter((lc) => this.isLcUpForRenewal(lc));
        return {
          type,
          title: 'Upcoming Invoice Due',
          description: 'LCs (not paused, lost, or internal) with a payment due date in the next 90 days, or already overdue/lapsed.',
          emptyLabel: 'No LCs currently have an upcoming or overdue invoice.',
          headers: ['LC', 'Payment Due', 'Details', 'Notes'],
          rows: this.renewalModalLcs.map((lc) => this.mapRenewalLcRow(lc))
        };
      case 'tokens':
        return {
          type,
          title: 'Tokens Total',
          description: 'Token usage by LC with role-level used and unused counts.',
          emptyLabel: 'No token data is available.',
          headers: ['LC', 'Token Type', 'Used', 'Unused', 'Total'],
          rows: []
        };
      case 'actions':
        return {
          type,
          title: 'Action Items',
          description: 'CRM record LC issues including missing LC links and missing contact or billing info.',
          emptyLabel: 'No action items are available.',
          headers: ['Severity', 'LC', 'Issue', 'Deadline', 'Details'],
          rows: []
        };
    }
  }

  private getRenewalPaymentDueDate(lc: CrmDashboardLcSnapshot): string | null {
    if (!lc.networkInfo) {
      return null;
    }

    return lc.networkInfo.next_payment_due_date || lc.networkInfo.next_invoice_date || null;
  }

  private isLcUpForRenewal(lc: CrmDashboardLcSnapshot): boolean {
    if (lc.stage === 'paused' || lc.stage === 'lost' || lc.type === 'internal') {
      return false;
    }

    const paymentDueDateValue = this.getRenewalPaymentDueDate(lc);

    if (!paymentDueDateValue) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const paymentDueDate = new Date(paymentDueDateValue);
    paymentDueDate.setHours(0, 0, 0, 0);
    const daysUntilPaymentDue = (paymentDueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilPaymentDue <= 90;
  }

  private isLcRenewalOverdue(lc: CrmDashboardLcSnapshot): boolean {
    const paymentDueDateValue = this.getRenewalPaymentDueDate(lc);

    if (!paymentDueDateValue) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const paymentDueDate = new Date(paymentDueDateValue);
    paymentDueDate.setHours(0, 0, 0, 0);
    return paymentDueDate.getTime() < today.getTime();
  }

  private getRenewalPaymentDueSortValue(lc: CrmDashboardLcSnapshot): number {
    const paymentDueDateValue = this.getRenewalPaymentDueDate(lc);
    if (!paymentDueDateValue) {
      return Number.POSITIVE_INFINITY;
    }

    const time = new Date(paymentDueDateValue).getTime();
    return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
  }

  private mapVendorModalSummaryRow(summary: CrmVendorModalSummary): string[] {
    return [
      summary.label,
      String(summary.clientCount),
      String(summary.lcCount)
    ];
  }

  private getDashboardActionIssues(): CrmDashboardActionIssue[] {
    const clientActionLcTargets = this.getClientActionLcTargets();
    const items: CrmDashboardActionIssue[] = [];

    this.addCustomClientActionItems(items);

    clientActionLcTargets.forEach((target) => {
      if (!this.hasPrimaryContactInfo(target.network)) {
        items.push({
          severity: 'Critical',
          lcLabel: target.lcLabel,
          detailsLink: target.detailsLink,
          issue: 'missing primary contact',
          deadline: null,
          details: 'Primary contact: No Info',
          sortTime: 0
        });
      }

      if (!this.hasBillingInfo(target.network)) {
        items.push({
          severity: 'Low',
          lcLabel: target.lcLabel,
          detailsLink: target.detailsLink,
          issue: 'missing billing contact',
          deadline: null,
          details: 'Billing contact: No Info',
          sortTime: 0
        });
      }
    });

    this.addClientWithoutLcActionItems(items);

    return this.sortDashboardActionIssues(items);
  }

  private addCustomClientActionItems(items: CrmDashboardActionIssue[]): void {
    (this.actionQueue || []).forEach((action) => {
      if (!action.customAction || action.status !== 'open') {
        return;
      }

      const target = this.getCustomActionTarget(action);
      items.push({
        severity: this.getCustomActionSeverity(action.level),
        lcLabel: target.label,
        detailsLink: target.detailsLink,
        issue: this.getCustomActionIssue(action),
        deadline: action.actionDate || null,
        details: this.getCustomActionDetails(action),
        sortTime: this.dateSortValue(action.actionDate)
      });
    });
  }

  private getCustomActionTarget(action: CrmActionItem): { label: string; detailsLink: string[] } {
    const networkId = Number(action.networkId || 0);
    if (networkId > 0) {
      const linkedNetwork = this.getNetworkByLinkedNetworkId(networkId);
      const linkedName = linkedNetwork ? this.getLinkedNetworkName(linkedNetwork, networkId) : '';
      return {
        label: `${linkedName || 'LC'} (#${networkId})`,
        detailsLink: this.getLcDetailsLink(networkId)
      };
    }

    const companyId = Number(action.companyId || 0);
    const network = this.networks.find((row) => Number(row.company_id || row.id || 0) === companyId);
    if (network) {
      const attachedLink = this.getAttachedLinkedNetworks(network)[0];
      if (attachedLink && attachedLink.network_id > 0) {
        return {
          label: `${attachedLink.network_name || network.name || 'LC'} (#${attachedLink.network_id})`,
          detailsLink: this.getLcDetailsLink(attachedLink.network_id)
        };
      }

      return {
        label: `${network.name || 'Record'} (#${companyId})`,
        detailsLink: this.getClientDetailsLinkByCompanyId(companyId)
      };
    }

    const standalone = this.noLcClientSnapshots.find((snapshot) => snapshot.companyId === companyId);
    return {
      label: `${standalone ? standalone.clientName : 'Record'} (#${companyId || action.id})`,
      detailsLink: this.getClientDetailsLinkByCompanyId(companyId || action.id)
    };
  }

  private getNetworkByLinkedNetworkId(networkId: number): CrmNetwork | null {
    return this.networks.find((network) =>
      this.getAttachedLinkedNetworks(network).some((link) => link.network_id === networkId)
    ) || null;
  }

  private getLinkedNetworkName(network: CrmNetwork, networkId: number): string {
    const link = this.getAttachedLinkedNetworks(network).find((candidate) => candidate.network_id === networkId);
    return (link && link.network_name) || network.lc_name || network.name || '';
  }

  private getCustomActionSeverity(level: string | null | undefined): CrmActionItemSeverity {
    return this.actionSeverityOptions.includes(level as CrmActionItemSeverity)
      ? level as CrmActionItemSeverity
      : 'Medium';
  }

  private getCustomActionIssue(action: CrmActionItem): string {
    if (action.kind === 'other') {
      return 'Other';
    }

    return this.formatIssueLabel(action.kind);
  }

  private getCustomActionDetails(action: CrmActionItem): string {
    return action.details || action.summary || '-';
  }

  private addClientWithoutLcActionItems(items: CrmDashboardActionIssue[]): void {
    this.noLcClientSnapshots.forEach((snapshot) => {
      if (snapshot.stage === 'paused' || snapshot.stage === 'lost' || snapshot.type === 'internal') {
        return;
      }

      items.push({
        severity: 'Medium',
        lcLabel: `${snapshot.clientName} (#${snapshot.companyId})`,
        detailsLink: this.getClientDetailsLinkByCompanyId(snapshot.companyId),
        issue: 'Record without LC',
        deadline: null,
        details: 'No LC attached',
        sortTime: 0
      });
    });
  }

  private getClientActionLcTargets(): CrmActionItemLcTarget[] {
    const targets: CrmActionItemLcTarget[] = [];

    this.networks.forEach((network) => {
      if (network.stage !== 'client' || network.type === 'internal') {
        return;
      }

      this.getAttachedLinkedNetworks(network).forEach((link) => {
        targets.push({
          networkID: link.network_id,
          lcLabel: `${link.network_name} (#${link.network_id})`,
          network,
          detailsLink: this.getLcDetailsLink(link.network_id)
        });
      });
    });

    return targets;
  }

  private getLcDetailsLink(networkId: number): string[] {
    return ['/crm', 'details', `${networkId}`];
  }

  private getClientDetailsLinkByCompanyId(companyId: number): string[] {
    return ['/crm', 'details', 'client', `${companyId}`];
  }

  private getVendorMetricDetailsLink(vendorLabel: string): string[] | null {
    const vendorSummary = this.getVendorModalSummaries().find((summary) => summary.label === vendorLabel);
    const vendorId = vendorSummary ? Number(vendorSummary.value) : 0;
    return Number.isFinite(vendorId) && vendorId > 0 ? this.getLcDetailsLink(vendorId) : null;
  }

  private getMetricRecordDetailsLink(row: string[]): string[] | null {
    const lcLink = this.getMetricRowLcDetailsLink(row[1] || '');

    if (lcLink) {
      return lcLink;
    }

    const companyId = this.extractMetricRowId(row[0] || '');
    return companyId > 0 ? this.getClientDetailsLinkByCompanyId(companyId) : null;
  }

  private getMetricRowLcDetailsLink(label: string): string[] | null {
    const networkId = this.extractMetricRowId(label);
    return Number.isFinite(networkId) && networkId > 0 ? this.getLcDetailsLink(networkId) : null;
  }

  private extractMetricRowId(label: string): number {
    const match = label.match(/\(#(\d+)\)/);
    const id = match ? Number(match[1]) : 0;
    return Number.isFinite(id) ? id : 0;
  }

  private sortDashboardActionIssues(items: CrmDashboardActionIssue[]): CrmDashboardActionIssue[] {
    return items.sort((left, right) => (
      this.getActionSeverityRank(right.severity) - this.getActionSeverityRank(left.severity)
      || left.issue.localeCompare(right.issue)
      || left.sortTime - right.sortTime
      || left.lcLabel.localeCompare(right.lcLabel)
    ));
  }

  private sortActionModalIssues(items: CrmDashboardActionIssue[]): CrmDashboardActionIssue[] {
    const sortColumn = this.actionModalSortColumn;
    if (sortColumn === null) {
      return items;
    }

    const direction = this.actionModalSortDirection === 'asc' ? 1 : -1;
    return [...items].sort((left, right) => {
      const comparison = this.compareActionModalIssues(left, right, sortColumn);
      return comparison === 0
        ? left.lcLabel.localeCompare(right.lcLabel)
        : comparison * direction;
    });
  }

  private rebuildActionModalIssues(): void {
    this.actionModalIssues = this.getDashboardActionIssues();
    this.actionModalSeveritySummaries = this.buildActionSeveritySummaries(this.actionModalIssues);
    this.actionModalIssueOptions = Array.from(new Set(this.actionModalIssues.map((item) => item.issue)))
      .sort((left, right) => left.localeCompare(right));

    if (this.actionModalIssueFilter !== 'all' && !this.actionModalIssueOptions.includes(this.actionModalIssueFilter)) {
      this.actionModalIssueFilter = 'all';
    }

    this.refreshActionModalRows();
  }

  private refreshActionModalRows(): void {
    const lcSearch = this.actionModalLcSearchTerm.trim().toLowerCase();
    const items = this.actionModalIssues.filter((item) => {
      const matchesSeverity = this.actionModalSeverityFilter === 'all' || item.severity === this.actionModalSeverityFilter;
      const matchesIssue = this.actionModalIssueFilter === 'all' || item.issue === this.actionModalIssueFilter;
      const matchesLc = !lcSearch || item.lcLabel.toLowerCase().includes(lcSearch);
      const matchesDueDate = !this.actionModalHasDueDateOnly || !!item.deadline;
      return matchesSeverity && matchesIssue && matchesLc && matchesDueDate;
    });

    this.filteredActionModalIssues = this.sortActionModalIssues(items);
    this.totalActionModalPages = Math.ceil(this.filteredActionModalIssues.length / this.actionModalItemsPerPage);

    if (this.totalActionModalPages === 0) {
      this.actionModalPage = 1;
      this.currentActionModalPage = 1;
      this.actionModalRangeStart = 0;
      this.actionModalRangeEnd = 0;
      this.paginatedActionModalIssues = [];
      return;
    }

    this.currentActionModalPage = Math.min(this.actionModalPage, this.totalActionModalPages);
    this.actionModalPage = this.currentActionModalPage;
    const startIndex = (this.currentActionModalPage - 1) * this.actionModalItemsPerPage;
    this.paginatedActionModalIssues = this.filteredActionModalIssues.slice(startIndex, startIndex + this.actionModalItemsPerPage);
    this.actionModalRangeStart = startIndex + 1;
    this.actionModalRangeEnd = Math.min(this.currentActionModalPage * this.actionModalItemsPerPage, this.filteredActionModalIssues.length);
  }

  private refreshRecordModalRows(): void {
    const rows = this.metricModalState && this.metricModalState.type === 'lcs'
      ? this.metricModalState.rows
      : [];
    const recordSearch = this.recordModalRecordSearchTerm.trim().toLowerCase();
    const lcSearch = this.recordModalLcSearchTerm.trim().toLowerCase();
    const stageLabel = this.recordModalStageFilter === 'all'
      ? ''
      : this.formatBreakdownLabel(this.recordModalStageFilter).toLowerCase();
    const typeLabel = this.recordModalTypeFilter === 'all'
      ? ''
      : this.formatBreakdownLabel(this.recordModalTypeFilter).toLowerCase();

    this.filteredRecordModalRows = rows.filter((row) => {
      const recordValue = (row[0] || '').toLowerCase();
      const lcValue = (row[1] || '').toLowerCase();
      const stageValue = (row[2] || '').toLowerCase();
      const typeValue = (row[3] || '').toLowerCase();
      const matchesRecord = !recordSearch || recordValue.includes(recordSearch);
      const matchesLc = !lcSearch || lcValue.includes(lcSearch);
      const matchesStage = this.recordModalStageFilter === 'all' || stageValue === stageLabel;
      const matchesType = this.recordModalTypeFilter === 'all' || typeValue === typeLabel;
      return matchesRecord && matchesLc && matchesStage && matchesType;
    });

    this.totalRecordModalPages = Math.ceil(this.filteredRecordModalRows.length / this.recordModalItemsPerPage);

    if (this.totalRecordModalPages === 0) {
      this.recordModalPage = 1;
      this.currentRecordModalPage = 1;
      this.recordModalRangeStart = 0;
      this.recordModalRangeEnd = 0;
      this.paginatedRecordModalRows = [];
      return;
    }

    this.currentRecordModalPage = Math.min(this.recordModalPage, this.totalRecordModalPages);
    this.recordModalPage = this.currentRecordModalPage;
    const startIndex = (this.currentRecordModalPage - 1) * this.recordModalItemsPerPage;
    this.paginatedRecordModalRows = this.filteredRecordModalRows.slice(startIndex, startIndex + this.recordModalItemsPerPage);
    this.recordModalRangeStart = startIndex + 1;
    this.recordModalRangeEnd = Math.min(this.currentRecordModalPage * this.recordModalItemsPerPage, this.filteredRecordModalRows.length);
  }

  private refreshRenewalModalRows(): void {
    const lcSearch = this.renewalModalLcSearchTerm.trim().toLowerCase();

    let lcs = this.renewalModalLcs.filter((lc) => {
      const lcValue = `${lc.link.network_name} (#${lc.link.network_id})`.toLowerCase();
      return !lcSearch || lcValue.includes(lcSearch);
    });

    if (this.renewalModalSortDirection) {
      const direction = this.renewalModalSortDirection === 'asc' ? 1 : -1;
      lcs = [...lcs].sort((left, right) => (this.getRenewalPaymentDueSortValue(left) - this.getRenewalPaymentDueSortValue(right)) * direction);
    }

    this.filteredRenewalModalRows = lcs.map((lc) => this.mapRenewalLcRow(lc));

    this.totalRenewalModalPages = Math.ceil(this.filteredRenewalModalRows.length / this.renewalModalItemsPerPage);

    if (this.totalRenewalModalPages === 0) {
      this.renewalModalPage = 1;
      this.currentRenewalModalPage = 1;
      this.renewalModalRangeStart = 0;
      this.renewalModalRangeEnd = 0;
      this.paginatedRenewalModalRows = [];
      return;
    }

    this.currentRenewalModalPage = Math.min(this.renewalModalPage, this.totalRenewalModalPages);
    this.renewalModalPage = this.currentRenewalModalPage;
    const renewalStartIndex = (this.currentRenewalModalPage - 1) * this.renewalModalItemsPerPage;
    this.paginatedRenewalModalRows = this.filteredRenewalModalRows.slice(renewalStartIndex, renewalStartIndex + this.renewalModalItemsPerPage);
    this.renewalModalRangeStart = renewalStartIndex + 1;
    this.renewalModalRangeEnd = Math.min(this.currentRenewalModalPage * this.renewalModalItemsPerPage, this.filteredRenewalModalRows.length);
  }

  private buildActionSeveritySummaries(items: CrmDashboardActionIssue[]): CrmActionSeveritySummary[] {
    const counts: Record<CrmActionItemSeverity, number> = {
      Low: 0,
      Medium: 0,
      High: 0,
      Critical: 0
    };

    items.forEach((item) => {
      counts[item.severity] += 1;
    });

    return this.actionSeverityOptions.map((severity) => ({
      severity,
      count: counts[severity],
      summaryClass: this.getActionSeveritySummaryClass(severity)
    }));
  }

  private getActionSeveritySummaryClass(severity: CrmActionItemSeverity): string {
    switch (severity) {
      case 'Low':
        return 'action-severity-summary-low';
      case 'Medium':
        return 'action-severity-summary-medium';
      case 'High':
        return 'action-severity-summary-high';
      case 'Critical':
        return 'action-severity-summary-critical';
    }
  }

  private compareActionModalIssues(
    left: CrmDashboardActionIssue,
    right: CrmDashboardActionIssue,
    column: CrmActionModalSortColumn
  ): number {
    switch (column) {
      case 'severity':
        return this.getActionSeverityRank(left.severity) - this.getActionSeverityRank(right.severity);
      case 'issue':
        return left.issue.localeCompare(right.issue);
      case 'deadline':
        return this.dateSortValue(left.deadline) - this.dateSortValue(right.deadline);
      case 'lc':
      default:
        return left.lcLabel.localeCompare(right.lcLabel);
    }
  }

  private getActionSeverityRank(severity: CrmActionItemSeverity): number {
    switch (severity) {
      case 'Low':
        return 1;
      case 'Medium':
        return 2;
      case 'High':
        return 3;
      case 'Critical':
        return 4;
    }
  }

  private dateSortValue(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private refreshTokenModalRows(): void {
    this.tokenModalRows = this.buildTokenModalRows(this.paginatedTokenModalLcs);
  }

  private getTokenModalRoleRows(lc: CrmDashboardTokenLcSummary): CrmDashboardTokenRoleSummary[] {
    const tokenTypeSearch = this.tokenModalTokenTypeSearchTerm.trim().toLowerCase();

    return (lc.roles || []).filter((token) => {
      if (!token || token.total <= 0) {
        return false;
      }

      if (!tokenTypeSearch) {
        return true;
      }

      return [
        token.roleName,
        String(token.roleId),
        String(token.total || 0),
        String(token.used || 0),
        String(token.unused || 0)
      ].some((value) => value.toLowerCase().includes(tokenTypeSearch));
    });
  }

  private buildTokenModalRows(lcs: CrmDashboardTokenLcSummary[]): CrmTokenModalRow[] {
    const rows: CrmTokenModalRow[] = [];
    for (const lc of lcs) {
      const lcLabel = `${lc.networkName} (#${lc.networkID})`;
      const requestHistory = lc.requestHistory || [];
      const roleRows = this.getTokenModalRoleRows(lc);
      const lcRowspan = roleRows.length + 1;

      rows.push({
        key: `${lc.networkID}:total`,
        lcLabel,
        lcDetailsLink: this.getLcDetailsLink(lc.networkID),
        lcRowspan,
        showLcLabel: true,
        requestHistory,
        tokenType: 'Total',
        used: lc.used || 0,
        unused: lc.unused || 0,
        total: lc.total || 0,
        isTotal: true
      });

      for (const token of roleRows) {
        rows.push({
          key: `${lc.networkID}:${token.roleId}`,
          lcLabel,
          lcDetailsLink: this.getLcDetailsLink(lc.networkID),
          lcRowspan: 0,
          showLcLabel: false,
          requestHistory: [],
          tokenType: token.roleName,
          used: token.used || 0,
          unused: token.unused || 0,
          total: token.total || 0,
          isTotal: false
        });
      }
    }

    return rows;
  }

  getTokenRequestStatusClass(request: CrmDashboardTokenRequestHistory): string {
    switch (request.statusKey) {
      case 'approved':
        return 'token-request-approved';
      case 'rejected':
        return 'token-request-rejected';
      case 'pending':
      default:
        return 'token-request-pending';
    }
  }

  formatTokenRequestMetadata(request: CrmDashboardTokenRequestHistory): string {
    const date = formatPrintDate(request.dateCreated);
    const comments = (request.comments || '').trim();
    return comments ? `${date} - ${comments}` : date;
  }

  hasTokenRequestCostNotes(request: CrmDashboardTokenRequestHistory): boolean {
    return (request.costNotes || '').trim().length > 0;
  }

  hasTokenRequestPoNumber(request: CrmDashboardTokenRequestHistory): boolean {
    return (request.poNumber || '').trim().length > 0;
  }

  private mapLcSnapshotRow(lc: CrmDashboardLcSnapshot): string[] {
    return [
      `${lc.companyName} (#${lc.companyId})`,
      `${lc.link.network_name} (#${lc.link.network_id})`,
      this.formatBreakdownLabel(lc.stage),
      lc.type ? this.formatBreakdownLabel(lc.type) : '-',
      lc.primary_contact_name || 'No Primary Contact'
    ];
  }

  private mapNoLcClientRow(snapshot: CrmDashboardNoLcSnapshot): string[] {
    return [
      `${snapshot.clientName} (#${snapshot.companyId})`,
      'No LC',
      this.formatBreakdownLabel(snapshot.stage),
      snapshot.type ? this.formatBreakdownLabel(snapshot.type) : '-',
      snapshot.primary_contact_name || 'No Primary Contact'
    ];
  }

  private mapMissingPrimaryContactLcRow(lc: CrmDashboardLcSnapshot): string[] {
    return [
      `${lc.link.network_name} (#${lc.link.network_id})`,
      `${lc.companyName} (#${lc.companyId})`,
      this.formatBreakdownLabel(lc.stage),
      lc.type ? this.formatBreakdownLabel(lc.type) : '-',
      lc.source ? this.formatBreakdownLabel(lc.source) : '-'
    ];
  }

  private mapRenewalLcRow(lc: CrmDashboardLcSnapshot): string[] {
    const networkInfo = lc.networkInfo;
    const billing = lc.companyBilling;
    const paymentDue = this.getRenewalPaymentDueDate(lc);
    const overdue = this.isLcRenewalOverdue(lc);

    return [
      `${lc.link.network_name}\n(#${lc.link.network_id})`,
      formatPrintDate(paymentDue),
      [
        `Status: ${overdue ? 'Overdue' : 'Upcoming'}`,
        `Advanced Pay: ${networkInfo ? (Number(networkInfo.advanced_payment) > 0 ? 'Yes' : 'No') : '-'}`,
        `Paid Until: ${formatPrintDate(networkInfo ? networkInfo.paid_until_date : null)}`
      ].join('\n'),
      (networkInfo && networkInfo.notes) || (billing && billing.billing_instructions) || '-'
    ];
  }

  private mapTestModeLinkRow(lc: CrmDashboardLcSnapshot): string[] {
    return [
      `${lc.link.network_name} (#${lc.link.network_id})`,
      lc.companyName,
      lc.link.crm_status ? this.formatBreakdownLabel(lc.link.crm_status) : '-',
      lc.link.is_primary ? 'Primary' : 'Secondary',
      lc.link.test_mode_note || lc.link.notes || '-'
    ];
  }

  private getContactSearchTerms(network: CrmNetwork): string[] {
    const contact = network.primary_contact;
    if (!contact) {
      return [];
    }

    const firstName = contact.first_name || '';
    const lastName = contact.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const compactName = `${firstName}${lastName}`.trim();

    return [
      firstName,
      lastName,
      fullName,
      compactName,
      contact.email || '',
      contact.email_alt || '',
      contact.phone || '',
      contact.phone_norm || '',
      contact.phone_office || '',
      contact.phone_cell || '',
      contact.phone_other || '',
      contact.phone_alt || ''
    ].filter((value) => value.length > 0);
  }

  private buildBreakdown(labels: Array<string | null | undefined>, preferredOrder: string[] = []): CrmBreakdownItem[] {
    const normalizedLabels = labels
      .filter((label): label is string => typeof label === 'string' && label.trim().length > 0)
      .map((label) => label.trim());
    const counts = normalizedLabels.reduce((map, label) => {
      map.set(label, (map.get(label) || 0) + 1);
      return map;
    }, new Map<string, number>());

    const orderedKeys = preferredOrder.filter((label) => counts.has(label));
    const unorderedKeys = Array.from(counts.keys())
      .filter((label) => !preferredOrder.includes(label))
      .sort((left, right) => left.localeCompare(right));

    return [...orderedKeys, ...unorderedKeys].map((label) => ({
      key: label,
      label: this.formatBreakdownLabel(label),
      count: counts.get(label) || 0
    }));
  }

  private getDashboardSummaryCount(items: CrmDashboardSummary[], key: string): number {
    const item = items.find((summary) => summary.key === key) || null;
    return item ? item.count : 0;
  }

  private formatContactFieldLabel(key: string): string {
    return key
      .replace(/[_-]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private formatContactFieldValue(value: CrmContactValue): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (Array.isArray(value) || typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  private getLinkedNetworks(network: CrmNetwork): CrmCompanyNetworkLink[] {
    return network.linked_networks && network.linked_networks.length > 0
      ? network.linked_networks
      : network.primary_network ? [network.primary_network] : [];
  }

  private getAttachedLinkedNetworks(network: CrmNetwork): CrmCompanyNetworkLink[] {
    return this.getLinkedNetworks(network).filter((link) => this.hasAttachedLc(link));
  }

  private getUnattachedLinkedNetworks(network: CrmNetwork): CrmCompanyNetworkLink[] {
    return this.getLinkedNetworks(network).filter((link) => !this.hasAttachedLc(link));
  }

  private hasAttachedLc(link: CrmCompanyNetworkLink): boolean {
    return link.network_id > 0;
  }

  private getDefaultCreateClientForm(): CrmCreateClientFormValue {
    return {
      clientName: '',
      legalName: '',
      stage: 'prospect',
      type: 'lead',
      source: '',
      industry: '',
      website: '',
      companyPhone: '',
      companyEmail: '',
      addressStreet1: '',
      addressStreet2: '',
      addressCity: '',
      addressRegion: '',
      addressPostalCode: '',
      addressCountry: '',
      note: '',
      clientNote: '',
      tagsCsv: '',
      ownerUserId: '',
      converted: '',
      mailingStreet1: '',
      mailingStreet2: '',
      mailingCity: '',
      mailingRegion: '',
      mailingPostalCode: '',
      mailingCountry: '',
      physicalStreet1: '',
      physicalStreet2: '',
      physicalCity: '',
      physicalRegion: '',
      physicalPostalCode: '',
      physicalCountry: '',
      firstContact: '',
      lastContact: '',
      dueDate: '',
      dueAction: '',
      invoiceReference: '',
      supportTicketReference: '',
      contactTypeKeys: [],
      contactTypeOther: '',
      departmentKeys: [],
      linkedNetworkIds: [],
      primaryNetworkId: null,
      contactFirstName: '',
      contactLastName: '',
      contactNickName: '',
      contactDepartment: '',
      contactTitle: '',
      contactPosition: '',
      contactEmail: '',
      contactAltEmail: '',
      contactPhone: '',
      contactOfficePhone: '',
      contactOfficeExtension: '',
      contactCellPhone: '',
      contactOtherPhone: '',
      contactOtherPhoneNote: '',
      contactAltPhone: '',
      preferredContact: 'email',
      contactNote: '',
      contactUserId: '',
      contactRolesCsv: '',
      contactTagsCsv: '',
      billingFirstName: '',
      billingLastName: '',
      billingContactName: '',
      billingEmail: '',
      billingPhone: '',
      billingStreet1: '',
      billingStreet2: '',
      billingCity: '',
      billingRegion: '',
      billingPostalCode: '',
      billingCountry: '',
      primaryRegistrarEmail: '',
      alternateAccountContactName: '',
      alternateAccountContactEmail: '',
      alternateAccountContactPhone: '',
      billingInstructions: '',
      referralSourceCompanyId: '',
      referralSourceCompanyName: '',
      referralSourceContactId: '',
      referralSourceContactName: '',
      referralProgram: '',
      referredByNameRaw: '',
      referred: '',
      referralNotes: ''
    };
  }

  private getDefaultCreateClientSections(): Record<CrmCreateClientSectionKey, boolean> {
    return {
      record: true,
      primaryContact: false,
      billing: false,
      referral: false
    };
  }

  private getCreateClientBillingContactName(): string {
    const nameParts = [
      this.createClientForm.billingFirstName,
      this.createClientForm.billingLastName
    ]
      .map((value) => value.trim())
      .filter((value) => value.length > 0);

    return nameParts.length > 0
      ? nameParts.join(' ')
      : this.createClientForm.billingContactName.trim();
  }

  private buildCreateClientPrimaryContact(
    nextContactId: number,
    nextVendorId: number,
    nextCompanyId: number,
    createdTimestamp: string
  ): CrmContact | null {
    const form = this.createClientForm;
    const primaryContactValues = [
      form.contactFirstName,
      form.contactLastName,
      form.contactNickName,
      form.contactDepartment,
      form.contactTitle,
      form.contactPosition,
      form.contactEmail,
      form.contactAltEmail,
      form.contactPhone,
      form.contactOfficePhone,
      form.contactOfficeExtension,
      form.contactCellPhone,
      form.contactOtherPhone,
      form.contactOtherPhoneNote,
      form.contactAltPhone,
      form.contactNote,
      form.contactUserId,
      form.contactRolesCsv,
      form.contactTagsCsv
    ];

    if (!primaryContactValues.some((value) => value.trim().length > 0)) {
      return null;
    }

    const firstName = form.contactFirstName.trim();
    const lastName = form.contactLastName.trim();
    const email = this.nullIfEmpty(form.contactEmail);
    const duplicateKey = [firstName, lastName]
      .filter((value) => value.length > 0)
      .join('-')
      .toLowerCase() || (email || `contact-${nextContactId}`).toLowerCase();

    return {
      id: nextContactId,
      vendor_id: nextVendorId,
      first_name: firstName,
      last_name: lastName,
      nick_name: this.nullIfEmpty(form.contactNickName),
      department: this.nullIfEmpty(form.contactDepartment),
      title: this.nullIfEmpty(form.contactTitle),
      position: this.nullIfEmpty(form.contactPosition) || this.nullIfEmpty(form.contactTitle),
      email,
      email_alt: this.nullIfEmpty(form.contactAltEmail),
      phone: this.nullIfEmpty(form.contactPhone),
      phone_office: this.nullIfEmpty(form.contactOfficePhone) || this.nullIfEmpty(form.contactPhone),
      phone_extension: this.nullIfEmpty(form.contactOfficeExtension),
      phone_cell: this.nullIfEmpty(form.contactCellPhone),
      phone_other: this.nullIfEmpty(form.contactOtherPhone),
      phone_other_note: this.nullIfEmpty(form.contactOtherPhoneNote),
      phone_alt: this.nullIfEmpty(form.contactAltPhone),
      phone_norm: this.normalizePhone(form.contactPhone),
      preferred_contact: this.normalizePreferredContact(form.preferredContact),
      address_json: null,
      contact_note: this.nullIfEmpty(form.contactNote),
      duplicate_key: duplicateKey,
      company_id: nextCompanyId,
      user_id: this.parseOptionalNumber(form.contactUserId),
      role: this.parseCsvList(form.contactRolesCsv),
      is_primary_for_company: true,
      tags_csv: this.nullIfEmpty(form.contactTagsCsv),
      created: createdTimestamp,
      modified: createdTimestamp,
      deleted: null
    };
  }

  private getNextNetworkId(): number {
    return this.networks.reduce((maxId, network) => Math.max(maxId, network.id), 1000) + 1;
  }

  private getNextContactId(): number {
    return this.networks.reduce((maxId, network) => Math.max(maxId, network.primary_contact ? network.primary_contact.id : maxId), 5000) + 1;
  }

  private getNextBillingId(): number {
    return this.networks.reduce((maxId, network) => Math.max(maxId, network.company_billing ? network.company_billing.id : maxId), 6000) + 1;
  }

  private getNextLinkId(): number {
    return this.networks.reduce((maxId, network) => {
      const linkedNetworks = this.getLinkedNetworks(network);
      const linkMaxId = linkedNetworks.reduce((innerMaxId, link) => Math.max(innerMaxId, link.id), maxId);
      return Math.max(maxId, linkMaxId);
    }, 7000) + 1;
  }

  private getNextReferralId(): number {
    return this.networks.reduce((maxId, network) => Math.max(maxId, network.referral_record ? network.referral_record.id : maxId), 8000) + 1;
  }

  private buildAddress(
    street1: string,
    street2: string,
    city: string,
    region: string,
    postalCode: string,
    country: string
  ): { street_1: string; street_2: string | null; city: string; region: string; postal_code: string; country: string; } | null {
    const normalizedStreet1 = street1.trim();
    const normalizedStreet2 = street2.trim();
    const normalizedCity = city.trim();
    const normalizedRegion = region.trim();
    const normalizedPostalCode = postalCode.trim();
    const normalizedCountry = country.trim();

    if (![normalizedStreet1, normalizedStreet2, normalizedCity, normalizedRegion, normalizedPostalCode, normalizedCountry].some((value) => value.length > 0)) {
      return null;
    }

    return {
      street_1: normalizedStreet1,
      street_2: normalizedStreet2.length > 0 ? normalizedStreet2 : null,
      city: normalizedCity,
      region: normalizedRegion,
      postal_code: normalizedPostalCode,
      country: normalizedCountry
    };
  }

  private buildAltenateAccountContact(
    name: string | null | undefined,
    email: string | null | undefined,
    phone: string | null | undefined
  ): { name: string; email: string; phone: string; } | null {
    const normalizedName = (name || '').trim();
    const normalizedEmail = (email || '').trim();
    const normalizedPhone = (phone || '').trim();

    if (![normalizedName, normalizedEmail, normalizedPhone].some((value) => value.length > 0)) {
      return null;
    }

    return {
      name: normalizedName,
      email: normalizedEmail,
      phone: normalizedPhone
    };
  }

  private buildReferralRecord(referralId: number, companyId: number, createdTimestamp: string) {
    const sourceCompanyId = this.parseOptionalNumber(this.createClientForm.referralSourceCompanyId);
    const sourceCompanyName = this.nullIfEmpty(this.createClientForm.referralSourceCompanyName);
    const sourceContactId = this.parseOptionalNumber(this.createClientForm.referralSourceContactId);
    const sourceContactName = this.nullIfEmpty(this.createClientForm.referralSourceContactName);
    const program = this.nullIfEmpty(this.createClientForm.referralProgram);
    const referredByNameRaw = this.nullIfEmpty(this.createClientForm.referredByNameRaw);
    const referred = this.parseOptionalDateTime(this.createClientForm.referred);
    const notes = this.nullIfEmpty(this.createClientForm.referralNotes);

    if (![sourceCompanyId, sourceCompanyName, sourceContactId, sourceContactName, program, referredByNameRaw, referred, notes].some((value) => value !== null)) {
      return null;
    }

    return {
      id: referralId,
      referred_company_id: companyId,
      source_company_id: sourceCompanyId,
      source_contact_id: sourceContactId,
      program,
      referred_by_name_raw: referredByNameRaw,
      referred,
      notes,
      created: createdTimestamp,
      modified: createdTimestamp,
      source_company_name: sourceCompanyName,
      source_contact_name: sourceContactName
    };
  }

  private createCompanyNetworkLink(option: CrmPrimaryNetworkOption, isPrimary: boolean, linkId: number, companyId: number): CrmCompanyNetworkLink {
    const now = this.formatTimestamp(new Date());
    return {
      id: linkId,
      company_id: companyId,
      network_id: option.network_id,
      network_name: option.network_name,
      is_primary: isPrimary,
      vendorId: option.vendorId,
      vendorName: option.vendorName || null,
      vendorUnknown: option.vendorUnknown,
      vendor: option.vendor,
      test_mode: option.test_mode,
      test_mode_note: option.test_mode_note,
      crm_status: option.crm_status,
      notes: option.notes,
      created: now,
      modified: now
    };
  }

  private formatDateOnly(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private formatTimestamp(date: Date): string {
    return date.toISOString().slice(0, 19);
  }

  private normalizePhone(value: string): string | null {
    const normalized = value.replace(/\D/g, '');
    return normalized.length > 0 ? normalized : null;
  }

  private normalizePreferredContact(value: string | null | undefined): 'email' | 'phone' {
    return (value || '').trim().toLowerCase() === 'phone' ? 'phone' : 'email';
  }

  private getPreferredContactValue(preferredContact: string | null | undefined, email: string | null | undefined, phone: string | null | undefined): string {
    const emailValue = (email || '').trim();
    const phoneValue = (phone || '').trim();

    return this.normalizePreferredContact(preferredContact) === 'phone'
      ? phoneValue || emailValue
      : emailValue || phoneValue;
  }

  private getBillingContactRecord(network: CrmNetwork): CrmContact | null {
    const billing = network.company_billing;
    const contacts = network.contacts || [];
    if (!billing || contacts.length === 0) {
      return null;
    }

    const billingName = this.normalizeContactMatchValue(billing.billing_contact_name);
    const billingEmail = this.normalizeContactMatchValue(billing.billing_email);
    const billingPhone = this.normalizePhoneForComparison(billing.billing_phone);

    return contacts.find((contact) => {
      const contactName = this.normalizeContactMatchValue(`${contact.first_name || ''} ${contact.last_name || ''}`);
      const contactEmails = [
        contact.email,
        contact.email_alt
      ].map((value) => this.normalizeContactMatchValue(value));
      const contactPhones = [
        contact.phone,
        contact.phone_office,
        contact.phone_cell,
        contact.phone_alt,
        contact.phone_norm
      ].map((value) => this.normalizePhoneForComparison(value));

      return (!!billingName && contactName === billingName)
        || (!!billingEmail && contactEmails.includes(billingEmail))
        || (!!billingPhone && contactPhones.includes(billingPhone));
    }) || null;
  }

  private normalizeContactMatchValue(value: string | null | undefined): string {
    return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private normalizePhoneForComparison(value: string | null | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private nullIfEmpty(value: string | null | undefined): string | null {
    const trimmed = (value || '').trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private parseOptionalNumber(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private parseOptionalDateTime(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return null;
    }

    const normalized = trimmed.replace('T', ' ');
    return normalized.length === 16 ? `${normalized}:00` : normalized.slice(0, 19);
  }

  private parseCsvList(value: string): string[] {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  private tagItemsToCsv(items: CrmTagListItem[]): string {
    return items
      .map((item) => item.name ? item.name.trim() : '')
      .filter((item) => item.length > 0)
      .join(', ');
  }

  private hasRequiredClientFields(): boolean {
    return this.createClientForm.clientName.trim().length > 0
      && this.createClientForm.stage.trim().length > 0
      && this.createClientForm.type.trim().length > 0
      && this.hasRequiredContactPoint();
  }

  private hasRequiredContactPoint(): boolean {
    return this.createClientForm.companyEmail.trim().length > 0
      || this.createClientForm.companyPhone.trim().length > 0;
  }

  private hasValidLcSelection(): boolean {
    const { linkedNetworkIds, primaryNetworkId } = this.createClientForm;
    return linkedNetworkIds.length === 0 || (primaryNetworkId !== null && linkedNetworkIds.includes(primaryNetworkId));
  }

  private getCreateClientValidationError(): string | null {
    if (!this.hasRequiredClientFields()) {
      return 'Complete the required record fields (Record Name, Stage, Type, and at least one of Email or Phone) before creating the record.';
    }

    if (!this.hasValidLcSelection()) {
      return 'Select one primary LC when choosing one or more LCs.';
    }

    return null;
  }
}
